import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet';
import type { BottomSheetDefaultBackdropProps } from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheetBackdrop/types';
import { SignInPrompt } from '@/features/account/SignInPrompt';
import { FlyerImage, type FlyerImageSource } from '@/features/events/shared/FlyerImage';
import { getCoverEventPosterImage } from '@/features/events/shared/FocalEventPosterImage';
import { RsvpConfirmSheet } from '@/features/events/shared/RsvpConfirmSheet';
import { ReportSheet } from '@/features/reports/ReportSheet';
import { useReport } from '@/features/reports/useReport';
import { PosterViewer } from '@/features/events/shared/PosterViewer';
import { EventTagDot } from '@/features/events/shared/EventTagDot';
import { ErrorState } from '@/components/ui/async-state';
import { AppTopBar } from '@/components/ui/AppTopBar';
import { Icon } from '@/components/ui/icon';
import { SceneBackground } from '@/components/ui/scene';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { UnavailableState } from '@/components/ui/UnavailableState';
import { APIError } from '@/lib/api/api';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { useAuth } from '@/lib/auth/auth';
import { showGuestWritePrompt } from '@/features/market/guestWrite';
import { consumePendingRsvp, queuePendingRsvp } from '@/features/events/pendingEventAction';
import { rememberEventCampaignAttribution } from '@/features/events/eventCampaignAttribution';
import { handleReadOnlyMarketError } from '@/features/market/readOnlyMarket';
import { formatApproximateDistanceLabel, useUserOriginCoords } from '@/lib/location/locationOrigin';
import { shareLink } from '@/lib/platform/share';
import { addSentryActionBreadcrumb, createSentryInputTracker } from '@/lib/analytics/sentry';
import { cn } from '@/lib/utils';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import {
  EntryTypeCover,
  EntryTypeFree,
  EntryTypeFreeRSVP,
  EntryTypeRSVP,
  EntryTypeTicketed,
  EVENT_POSTER_CONSTANTS,
  RSVPVisibilityPublic,
  type RSVPVisibility,
} from '@/lib/api/apiTypes';
import {
  defaultRSVPVisibilityFromLegacyPreference,
  loadRSVPVisibilityPreference,
  saveRSVPVisibilityPreference,
} from '@/features/events/rsvpVisibilityPreference';
import {
  getEntryTypeBadgeLabel,
  getEntryPriceValue,
  getEntryTypeLabel,
  getEventAttributeLabel,
  formatEventAgeRestriction,
  getEventLimitNotice,
  isTicketedEventWithURL,
  normalizeExternalHTTPURL,
  shouldShowPrimaryTicketCTA,
  getEventTagLabel,
} from '@/features/events/eventTags';
import { resolveEventEntryDetails } from '@/features/events/eventMobileHelpers';
import {
  createLoadTimer,
  trackEmptyStateSeen,
  trackCalendarExportTapped,
  trackEventImageViewed,
  trackEventLinkTapped,
  trackCommentSubmitted,
  trackEventSaved,
  trackEventShare,
  trackEventUnsaved,
  trackTicketLinkTapped,
  trackEventView,
  trackEventViewDuration,
  trackRsvpFailed,
} from '@/lib/analytics/posthog';
import { useWritableMarketGuard } from '@/features/market/useWritableMarketGuard';
import { useDashboard } from '@/features/profile/useDashboard';
import { useCancelEvent } from '@/features/events/useCancelEvent';
import {
  CancelEventModal,
  CancelledEventBanner,
  EventDetailOverflowSheet,
  EventEndedBanner,
  HeroAttendeeSummary,
  HeroSection,
  ManageRsvpSheetContent,
  OwnerStatsCard,
  RsvpBottomSheet,
  formatDetailSheetTime,
} from '@/features/events/eventDetail/EventDetailSections';
import {
  AttendeeModal,
  CommentComposer,
  CommentComposerLauncher,
  CommentRow,
  WebReportModal,
} from '@/features/events/eventDetail/EventDetailComments';
import {
  DirectEntryDownloadBanner,
  LinkCopiedToast,
  SignInBanner,
  StickyActionBar,
} from '@/features/events/eventDetail/EventDetailChrome';
import {
  buildGoogleCalendarURL,
  getDetailEventHoursUntilStart,
  getStickyBarLabel,
  hasDetailEventEnded,
  isArchivedEvent,
  isCancelledEvent,
  isDetailEventFull,
  isDetailEventHappeningNow,
  isReadOnlyEvent,
  type RsvpSheetMode,
} from '@/features/events/eventDetail/eventDetailHelpers';
import { useEventDetail } from '@/features/events/useEventDetail';
import { useEventDetailMutations } from '@/features/events/useEventDetailMutations';
import { useUpdateProfile } from '@/features/profile/useUpdateProfile';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { useVenueDetail } from '@/features/venues/useVenueDetail';
import { getVenueCategoryConfig, resolveVenueCategory } from '@/features/venues/venueCategories';
import { type DiscoveryEventDetail as RadiousEventDetail } from '@/lib/api/apiTypes';
import { formatEventDayLabel, formatEventTime, formatEventTimeRange } from '@/features/events/eventFormatting';
import { buildEventLink, getEventHref, getEventPath, getPublicIdentityHref, getProfileHref, getRouteEntityID, getVenueHref } from '@/lib/navigation/routes';
import { useFocusEffect } from '@react-navigation/native';
import { Image } from 'expo-image';
import * as Clipboard from 'expo-clipboard';
import * as WebBrowser from 'expo-web-browser';
import { Stack, router, useLocalSearchParams, useSegments } from 'expo-router';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  CheckIcon,
  ChevronDownIcon,
  CloudRainIcon,
  CarIcon,
  ExternalLinkIcon,
  MoreHorizontalIcon,
  ShareIcon,
  ShirtIcon,
  TicketIcon,
  UserCircleIcon,
  XIcon,
} from 'lucide-react-native';
import * as React from 'react';
import {
  ActionSheetIOS,
  Animated,
  KeyboardAvoidingView,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type KeyboardEvent,
  useWindowDimensions,
} from 'react-native';
import Reanimated, { FadeIn, FadeOut, LinearTransition } from 'react-native-reanimated';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  subscribeToAppStateChange,
  subscribeToKeyboardEvent,
} from '@/lib/app/nativeEventSubscriptions';
import { getUniqueEventAttendees } from '@/features/events/eventAttendees';

const RADIOUS_ICON = require('../../../assets/images/icon.png');
const APP_STORE_URL = 'https://apps.apple.com/us/app/radious-local-events/id6760730711';
const DIRECT_EVENT_DOWNLOAD_BANNER_DISMISSED_KEY = 'radious:event-download-banner-dismissed';
const CONTAIN_TEXT_STYLE = Platform.select({
  web: { overflowWrap: 'anywhere', wordBreak: 'break-word' } as object,
});
const WEB_TITLE_CLIP_STYLE = Platform.select({
  web: { maxHeight: 31 * 5, overflow: 'hidden' } as object,
});
const WEB_DESCRIPTION_CLIP_STYLE = Platform.select({
  web: { maxHeight: 25 * 5, overflow: 'hidden' } as object,
});
const WEB_ENTRY_NOTES_CLIP_STYLE = Platform.select({
  web: { maxHeight: 16 * 4, overflow: 'hidden' } as object,
});
function readWebSessionValue(key: string) {
  if (Platform.OS !== 'web') {
    return null;
  }

  try {
    return window.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeWebSessionValue(key: string, value: string) {
  if (Platform.OS !== 'web') {
    return;
  }

  try {
    window.sessionStorage?.setItem(key, value);
  } catch {
    // Best-effort only.
  }
}

function openAppStore() {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.assign(APP_STORE_URL);
    return;
  }

  Linking.openURL(APP_STORE_URL).catch(() => undefined);
}

function stripTrailingSentencePeriod(value: string) {
  return value.trim().replace(/\.$/, '');
}

export function EventDetailLoadingSkeleton() {
  const { width: windowWidth, height: screenHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isWeb = Platform.OS === 'web';
  const detailColumnWidth = isWeb ? Math.max(320, Math.min(windowWidth - 24, 860)) : windowWidth;
  const heroHeight = isWeb
    ? Math.max(260, Math.min(480, Math.round(detailColumnWidth * 0.56)))
    : Math.round(screenHeight * 0.42);

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: DESIGN_TOKENS.colors.canvas }}>
      {isWeb ? null : <SceneBackground />}
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: heroHeight + insets.top,
          backgroundColor: DESIGN_TOKENS.colors.canvas,
        }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 24 }}
        style={{ backgroundColor: 'transparent' }}>
        <View
          style={{
            width: detailColumnWidth,
            alignSelf: 'center',
            paddingTop: isWeb ? 16 : 0,
          }}>
          <View
            className={`relative overflow-hidden ${isWeb ? 'rounded-[30px]' : ''}`}
            style={
              isWeb
                ? {
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.borderStrong,
                    backgroundColor: DESIGN_TOKENS.colors.surface,
                  }
                : undefined
            }>
            <View style={{ height: heroHeight, backgroundColor: DESIGN_TOKENS.colors.canvas }}>
              <View className="absolute inset-x-0 top-0 flex-row items-center justify-between px-4 pt-3">
                <Skeleton className="size-11 rounded-full bg-surface-raised/70" />
                <View className="flex-row gap-3">
                  <Skeleton className="size-11 rounded-full bg-surface-raised/70" />
                  <Skeleton className="size-11 rounded-full bg-surface-raised/70" />
                  <Skeleton className="size-11 rounded-full bg-surface-raised/70" />
                </View>
              </View>
              <View className="absolute inset-x-0 bottom-0 px-4 pb-5">
                <Skeleton className="h-9 w-4/5 bg-surface-raised/80" />
                <Skeleton className="mt-3 h-4 w-1/3 bg-surface-raised/70" />
              </View>
            </View>
          </View>

          <View className="px-4 pb-10" style={{ backgroundColor: DESIGN_TOKENS.colors.canvas }}>
            <View className="-mt-2 rounded-[24px] border border-border-strong bg-surface p-4">
              <View className="flex-row gap-3">
                <Skeleton className="h-12 flex-1 rounded-[18px] bg-surface-raised" />
                <Skeleton className="h-12 flex-1 rounded-[18px] bg-surface-raised" />
              </View>
              <Skeleton className="mt-3 h-12 rounded-[18px] bg-surface-raised" />
            </View>

            <View className="mt-4 rounded-[24px] border border-border-strong bg-surface p-4">
              <View className="flex-row items-start gap-3">
                <Skeleton className="size-10 rounded-full bg-surface-raised" />
                <View className="flex-1">
                  <Skeleton className="h-5 w-3/4 bg-surface-raised" />
                  <Skeleton className="mt-2 h-4 w-1/2 bg-surface-raised" />
                </View>
                <Skeleton className="size-10 rounded-full bg-surface-raised" />
              </View>
              <View className="my-4 h-px bg-border" />
              <View className="flex-row items-start gap-3">
                <Skeleton className="size-10 rounded-full bg-surface-raised" />
                <View className="flex-1">
                  <Skeleton className="h-5 w-5/6 bg-surface-raised" />
                  <Skeleton className="mt-2 h-4 w-2/5 bg-surface-raised" />
                </View>
              </View>
            </View>

            <View className="mt-4 rounded-[24px] border border-border-strong bg-surface p-4">
              <Skeleton className="h-4 w-20 bg-surface-raised" />
              <Skeleton className="mt-4 h-10 w-full rounded-[18px] bg-surface-raised" />
            </View>

            <View className="mt-4">
              <Skeleton className="h-4 w-full bg-surface-raised" />
              <Skeleton className="mt-3 h-4 w-[92%] bg-surface-raised" />
              <Skeleton className="mt-3 h-4 w-[84%] bg-surface-raised" />
              <Skeleton className="mt-3 h-4 w-[76%] bg-surface-raised" />
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

export default function EventDetailScreen() {
  const { id, feed_position, qr_slug, skeleton, source, utm_campaign, utm_medium, utm_source } =
    useLocalSearchParams<{
      id: string;
      feed_position?: string;
      qr_slug?: string;
      skeleton?: string;
      source?: string;
      utm_campaign?: string;
      utm_medium?: string;
      utm_source?: string;
    }>();
  const segments = useSegments();
  const shouldForceSkeleton = __DEV__ && (skeleton === '1' || id === 'skeleton');
  const eventID = shouldForceSkeleton ? '' : getRouteEntityID(id);
  const session = useAuth((state) => state.session);
  const userOrigin = useUserOriginCoords();
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const dashboardQuery = useDashboard();
  const { availableContexts, setBusinessContext } = useAccountContext();
  const updateProfileMutation = useUpdateProfile();
  const ensureWritableMarket = useWritableMarketGuard();
  const detailQuery = useEventDetail(eventID);
  const cancelEventMutation = useCancelEvent();
  const venueDetailQuery = useVenueDetail(detailQuery.data?.event.venue_id);
  const {
    confirmGoing: confirmGoingMutation,
    removeRsvp: removeRsvpMutation,
    toggleSaved: toggleSavedMutation,
    submitComment: submitCommentMutation,
    deleteComment: deleteCommentMutation,
    hideComment: hideCommentMutation,
    unhideComment: unhideCommentMutation,
  } = useEventDetailMutations();
  const [commentBody, setCommentBody] = React.useState('');
  const [isKeyboardVisible, setIsKeyboardVisible] = React.useState(false);
  const [keyboardHeight, setKeyboardHeight] = React.useState(0);
  const [isCommentComposerActive, setIsCommentComposerActive] = React.useState(false);
  const [isPosterViewerVisible, setIsPosterViewerVisible] = React.useState(false);
  const [posterViewerInitialIndex, setPosterViewerInitialIndex] = React.useState(0);
  const [isAttendeeModalVisible, setIsAttendeeModalVisible] = React.useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = React.useState(false);
  const [cancelMessage, setCancelMessage] = React.useState('');
  const [rsvpVisibility, setRsvpVisibility] = React.useState<RSVPVisibility>(RSVPVisibilityPublic);
  const [copied, setCopied] = React.useState(false);
  const [rsvpSheetMode, setRsvpSheetMode] = React.useState<RsvpSheetMode | null>(null);
  const [webGuestAction, setWebGuestAction] = React.useState<'rsvp' | 'manage' | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isDirectEntryDownloadBannerDismissed, setIsDirectEntryDownloadBannerDismissed] =
    React.useState(() => readWebSessionValue(DIRECT_EVENT_DOWNLOAD_BANNER_DISMISSED_KEY) === '1');
  const event = detailQuery.data?.event ?? null;
  const venueDetail = venueDetailQuery.data ?? null;
  const eventPosterImages = React.useMemo(() => {
    if (!event) {
      return [];
    }
    const posterImages = event.poster_images
      ?.filter((image) => image.public_url.trim().length > 0)
      .slice()
      .sort((left, right) => left.position - right.position);
    if (posterImages?.length) {
      return posterImages;
    }
    return event.poster_url
      ? [
          {
            public_url: event.poster_url,
            focal_point_x: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.x,
            focal_point_y: EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.y,
            focal_scale: 1,
          },
        ]
      : [];
  }, [event]);
  const directionsQuery = React.useMemo(() => {
    if (event?.address?.trim()) {
      return event.address.trim();
    }
    if (typeof venueDetail?.lat === 'number' && typeof venueDetail.lng === 'number') {
      return `${venueDetail.lat},${venueDetail.lng}`;
    }
    if (venueDetail?.address?.trim()) {
      return venueDetail.address.trim();
    }
    return null;
  }, [event?.address, venueDetail?.address, venueDetail?.lat, venueDetail?.lng]);
  const comments = detailQuery.data?.comments ?? [];
  const isLoading = detailQuery.isPending;
  const userCounty = session?.me.profile.last_county_name ?? null;
  const feedPosition = React.useMemo(() => {
    if (typeof feed_position !== 'string') {
      return null;
    }
    const parsed = Number.parseInt(feed_position, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [feed_position]);
  const analyticsEntrySource =
    source === 'share_link' || utm_source === 'share_link'
      ? 'share_link'
      : source === 'web_feed' || source === 'web_rail'
        ? 'feed'
        : source === 'search'
          ? 'search'
          : source === 'map'
            ? 'map'
            : undefined;
  const isSharedLinkEntry = analyticsEntrySource === 'share_link';
  const eventPath = event ? getEventPath(event.id, event.title) : null;
  const defaultRsvpPublic = dashboardQuery.data?.profile.default_rsvp_public ?? true;
  const isSubmittingComment = submitCommentMutation.isPending;
  const isRsvpMutationPending =
    confirmGoingMutation.isPending || removeRsvpMutation.isPending || toggleSavedMutation.isPending;
  const isHost = event ? event.host.user_id === session?.me.user.id : false;
  const eventManagementContext = React.useMemo(() => {
    if (!event?.venue_id) {
      return null;
    }
    for (const context of availableContexts) {
      if (context.kind !== 'business') {
        continue;
      }
      const role = context.membership.role;
      const canManageRole = role === 'owner' || role === 'manager' || role === 'social';
      const hasVenue = context.membership.venues.some((venue) => venue.venue_id === event.venue_id);
      if (canManageRole && hasVenue) {
        return {
          organizationID: context.membership.organization_id,
          venueID: event.venue_id,
        };
      }
    }
    return null;
  }, [availableContexts, event?.venue_id]);
  const {
    bottomSheetRef: reportSheetRef,
    target: reportTarget,
    openReportSheet,
    handleDismiss: handleReportDismiss,
    submitReport,
  } = useReport();
  const bottomSheetRef = React.useRef<BottomSheetModal>(null);
  const overflowSheetRef = React.useRef<BottomSheetModal>(null);
  const postTicketPromptSheetRef = React.useRef<BottomSheetModal>(null);
  const rsvpSheetPresentationTokenRef = React.useRef(0);
  const postTicketPromptShownEventIDsRef = React.useRef(new Set<string>());
  const pendingPostTicketPromptEventIDRef = React.useRef<string | null>(null);
  const scrollViewRef = React.useRef<ScrollView | null>(null);
  const loadTimerRef = React.useRef(createLoadTimer('event_detail'));
  const viewedEventIDRef = React.useRef<string | null>(null);
  const commentInputTracker = React.useMemo(
    () =>
      createSentryInputTracker({
        screen: 'event_detail',
        field: 'comment',
        form: 'event_thread',
        entityId: eventID,
      }),
    [eventID]
  );
  const renderBackdrop = React.useCallback(
    (props: BottomSheetDefaultBackdropProps) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />
    ),
    []
  );
  const snapPoints = React.useMemo(
    () => (rsvpSheetMode === 'manage' ? ['76%'] : ['82%']),
    [rsvpSheetMode]
  );
  const postTicketPromptSnapPoints = React.useMemo(() => [220], []);
  const canonicalEventHref = React.useMemo(() => {
    if (!event) {
      return null;
    }

    return getEventHref(event.id, event.title, feed_position ? { feed_position } : {});
  }, [event, feed_position]);
  const eventDetailAnalyticsSurface = Platform.OS === 'web' ? 'web' : undefined;

  React.useEffect(() => {
    if (Platform.OS !== 'web' || !canonicalEventHref || !id) {
      return;
    }

    if (id === canonicalEventHref.params?.id && segments[0] !== 'event') {
      return;
    }

    // Canonicalization waits for detail data so legacy/share URLs are replaced without redirect loops.
    router.replace(canonicalEventHref);
  }, [canonicalEventHref, id, segments]);

  const dismissDirectEntryDownloadBanner = React.useCallback(() => {
    setIsDirectEntryDownloadBannerDismissed(true);
    writeWebSessionValue(DIRECT_EVENT_DOWNLOAD_BANNER_DISMISSED_KEY, '1');
  }, []);

  const presentPostTicketPrompt = React.useCallback(
    (targetEvent: RadiousEventDetail) => {
      if (
        Platform.OS === 'web' ||
        targetEvent.viewer_rsvp_status === 'going' ||
        postTicketPromptShownEventIDsRef.current.has(targetEvent.id)
      ) {
        return;
      }

      postTicketPromptShownEventIDsRef.current.add(targetEvent.id);
      setRsvpVisibility(defaultRSVPVisibilityFromLegacyPreference(defaultRsvpPublic));
      loadRSVPVisibilityPreference(defaultRsvpPublic)
        .then(setRsvpVisibility)
        .catch(() => undefined);
      requestAnimationFrame(() => {
        postTicketPromptSheetRef.current?.present({ index: 0 });
      });
    },
    [defaultRsvpPublic]
  );

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const handleKeyboardShow = (event: KeyboardEvent) => {
      setIsKeyboardVisible(true);
      setKeyboardHeight(event.endCoordinates.height);
    };
    const handleKeyboardHide = () => {
      setIsKeyboardVisible(false);
      setKeyboardHeight(0);
      setIsCommentComposerActive(false);
    };
    const showSubscriptions = [
      subscribeToKeyboardEvent('keyboardWillShow', handleKeyboardShow),
      subscribeToKeyboardEvent('keyboardDidShow', handleKeyboardShow),
    ];
    const hideSubscriptions = [
      subscribeToKeyboardEvent('keyboardWillHide', handleKeyboardHide),
      subscribeToKeyboardEvent('keyboardDidHide', handleKeyboardHide),
    ];

    return () => {
      showSubscriptions.forEach((unsubscribe) => unsubscribe());
      hideSubscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, []);

  React.useEffect(() => {
    if (Platform.OS === 'web' || !isCommentComposerActive || keyboardHeight <= 0) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [isCommentComposerActive, keyboardHeight]);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    return subscribeToAppStateChange((nextState) => {
      if (
        nextState !== 'active' ||
        !event ||
        pendingPostTicketPromptEventIDRef.current !== event.id
      ) {
        return;
      }

      pendingPostTicketPromptEventIDRef.current = null;
      presentPostTicketPrompt(event);
    });
  }, [event, presentPostTicketPrompt]);

  React.useEffect(() => {
    if (!rsvpSheetMode) {
      return;
    }
    let isCancelled = false;
    setRsvpVisibility(defaultRSVPVisibilityFromLegacyPreference(defaultRsvpPublic));
    loadRSVPVisibilityPreference(defaultRsvpPublic)
      .then((visibility) => {
        if (!isCancelled) {
          setRsvpVisibility(visibility);
        }
      })
      .catch(() => undefined);

    return () => {
      isCancelled = true;
    };
  }, [defaultRsvpPublic, rsvpSheetMode]);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    if (!rsvpSheetMode) {
      bottomSheetRef.current?.dismiss();
      return;
    }

    rsvpSheetPresentationTokenRef.current += 1;
    const presentationToken = rsvpSheetPresentationTokenRef.current;
    const presentSheet = () => {
      // BottomSheet can race state/layout on the first frame; token + retry prevent stale opens.
      if (rsvpSheetPresentationTokenRef.current !== presentationToken || !rsvpSheetMode) {
        return;
      }

      bottomSheetRef.current?.present({ index: 0 });
    };
    const presentFrame = requestAnimationFrame(presentSheet);
    const retryTimer = setTimeout(presentSheet, 120);

    return () => {
      cancelAnimationFrame(presentFrame);
      clearTimeout(retryTimer);
    };
  }, [rsvpSheetMode]);

  const presentRsvpSheet = React.useCallback(
    (mode: RsvpSheetMode) => {
      rsvpSheetPresentationTokenRef.current += 1;
      const presentationToken = rsvpSheetPresentationTokenRef.current;
      setRsvpVisibility(defaultRSVPVisibilityFromLegacyPreference(defaultRsvpPublic));
      setRsvpSheetMode(mode);

      if (Platform.OS === 'web') {
        return;
      }

      requestAnimationFrame(() => {
        if (rsvpSheetPresentationTokenRef.current !== presentationToken) {
          return;
        }
        bottomSheetRef.current?.present({ index: 0 });
      });
    },
    [defaultRsvpPublic]
  );

  React.useEffect(() => {
    loadTimerRef.current = createLoadTimer('event_detail');
  }, [id]);

  React.useEffect(() => {
    if (detailQuery.isPending) {
      return;
    }

    loadTimerRef.current.done();

    if (!event) {
      trackEmptyStateSeen({
        screen: 'event_detail',
        userCounty,
        reason: 'error',
      });
    }
  }, [detailQuery.isPending, event, userCounty]);

  React.useEffect(() => {
    if (!event || viewedEventIDRef.current === event.id) {
      return;
    }

    viewedEventIDRef.current = event.id;
    void rememberEventCampaignAttribution(event.id, qr_slug);
    trackEventView({
      eventID: event.id,
      eventName: event.title,
      venueID: event.venue_id,
      venueName: event.venue_name,
      feedPosition,
      userCounty,
      distanceFromUser: event.distance_miles,
      eventDate: event.starts_at,
      eventType: event.tags?.[0] ?? 'unknown',
      qrSlug: qr_slug,
      source: analyticsEntrySource,
      surface: eventDetailAnalyticsSurface,
      utmCampaign: utm_campaign,
      utmMedium: utm_medium,
      utmSource: utm_source,
    });
  }, [
    analyticsEntrySource,
    event,
    eventDetailAnalyticsSurface,
    feedPosition,
    qr_slug,
    userCounty,
    utm_campaign,
    utm_medium,
    utm_source,
  ]);

  const eventDurationID = event?.id ?? null;
  const eventDurationVenueID = event?.venue_id ?? null;

  React.useEffect(() => {
    if (!eventDurationID) {
      return;
    }

    const startedAt = Date.now();
    return () => {
      trackEventViewDuration({
        eventID: eventDurationID,
        venueID: eventDurationVenueID,
        source: analyticsEntrySource ?? 'event_detail',
        surface: eventDetailAnalyticsSurface,
        secondsOnScreen: Math.round((Date.now() - startedAt) / 1000),
      });
    };
  }, [analyticsEntrySource, eventDetailAnalyticsSurface, eventDurationID, eventDurationVenueID]);

  const detailRefetchRef = React.useRef(detailQuery.refetch);

  React.useEffect(() => {
    detailRefetchRef.current = detailQuery.refetch;
  }, [detailQuery.refetch]);

  useFocusEffect(
    React.useCallback(() => {
      if (!id) {
        return;
      }

      detailRefetchRef.current().catch(() => undefined);
    }, [id])
  );

  const openTicketURL = React.useCallback(
    async (targetEvent: RadiousEventDetail, ticketURL: string) => {
      const safeTicketURL = normalizeExternalHTTPURL(ticketURL);
      if (!safeTicketURL) {
        setError('Could not open the ticket link. Try again in a moment.');
        return;
      }
      const shouldPromptOnReturn =
        Platform.OS !== 'web' &&
        targetEvent.viewer_rsvp_status !== 'going' &&
        !postTicketPromptShownEventIDsRef.current.has(targetEvent.id);

      if (shouldPromptOnReturn) {
        pendingPostTicketPromptEventIDRef.current = targetEvent.id;
      }

      try {
        await trackTicketLinkTapped({
          eventID: targetEvent.id,
          isPaid: true,
          surface: eventDetailAnalyticsSurface,
          venueID: targetEvent.venue_id,
        }).catch(() => undefined);
        await WebBrowser.openBrowserAsync(safeTicketURL);
        if (pendingPostTicketPromptEventIDRef.current === targetEvent.id) {
          pendingPostTicketPromptEventIDRef.current = null;
          presentPostTicketPrompt(targetEvent);
        }
      } catch {
        Linking.openURL(safeTicketURL).catch(() => {
          pendingPostTicketPromptEventIDRef.current = null;
          setError('Could not open the ticket link. Try again in a moment.');
        });
      }
    },
    [eventDetailAnalyticsSurface, presentPostTicketPrompt]
  );

  const closeRsvpSheet = React.useCallback(() => {
    if (Platform.OS !== 'web') {
      rsvpSheetPresentationTokenRef.current += 1;
      bottomSheetRef.current?.dismiss();
      // setRsvpSheetMode(null) is handled by onDismiss after animation completes
    } else {
      setRsvpSheetMode(null);
      setWebGuestAction(null);
    }
  }, []);

  const openConfirmRsvpSheet = React.useCallback(async () => {
    if (!event) {
      return;
    }
    if (isTicketedEventWithURL(event)) {
      const ticketURL = event.ticket_url?.trim();
      if (!ticketURL) {
        return;
      }
      await openTicketURL(event, ticketURL);
      return;
    }
    if (event.entry_type === EntryTypeTicketed) {
      setError('Ticket link is not available yet.');
      return;
    }
    if (isDetailEventFull(event)) {
      setError(getEventLimitNotice(event) ?? 'This event is at capacity.');
      return;
    }
    if (!session) {
      if (Platform.OS === 'web') {
        setWebGuestAction('rsvp');
        return;
      }
      showGuestWritePrompt('rsvp', { next: eventPath ?? undefined });
      return;
    }
    presentRsvpSheet('confirm');
    if (!(await ensureWritableMarket())) {
      closeRsvpSheet();
      return;
    }
  }, [
    closeRsvpSheet,
    ensureWritableMarket,
    event,
    eventPath,
    openTicketURL,
    presentRsvpSheet,
    session,
  ]);

  const openManageRsvpSheet = React.useCallback(async () => {
    if (!event) {
      return;
    }
    if (!session) {
      if (Platform.OS === 'web') {
        setWebGuestAction('manage');
        return;
      }
      showGuestWritePrompt('rsvp_manage', { next: eventPath ?? undefined });
      return;
    }
    presentRsvpSheet('manage');
    if (!(await ensureWritableMarket())) {
      closeRsvpSheet();
      return;
    }
  }, [closeRsvpSheet, ensureWritableMarket, event, eventPath, presentRsvpSheet, session]);

  const handleRsvpSheetDismiss = React.useCallback(() => {
    rsvpSheetPresentationTokenRef.current += 1;
    setRsvpSheetMode(null);
  }, []);

  const closePostTicketPrompt = React.useCallback(() => {
    postTicketPromptSheetRef.current?.dismiss();
  }, []);

  const changeRsvpVisibility = React.useCallback((visibility: RSVPVisibility) => {
    setRsvpVisibility(visibility);
    saveRSVPVisibilityPreference(visibility).catch(() => undefined);
  }, []);

  const submitGoingAfter = React.useCallback(
    async (closeSheet: () => void, closeTiming: 'before' | 'after' = 'before') => {
      if (!event || !session) {
        return;
      }

      const nextDefaultRsvpPublic = rsvpVisibility === RSVPVisibilityPublic;
      const shouldPersistSharePreference = nextDefaultRsvpPublic !== defaultRsvpPublic;

      setError(null);
      if (closeTiming === 'before') {
        closeSheet();
      }

      try {
        await confirmGoingMutation.mutateAsync({
          eventID: event.id,
          visibility: rsvpVisibility,
          source: 'detail',
        });
        await saveRSVPVisibilityPreference(rsvpVisibility).catch(() => undefined);
        if (shouldPersistSharePreference) {
          await updateProfileMutation
            .mutateAsync({ default_rsvp_public: nextDefaultRsvpPublic })
            .catch(() => undefined);
        }
        if (closeTiming === 'after') {
          closeSheet();
        }
      } catch (actionError) {
        trackRsvpFailed({
          eventID: event.id,
          venueID: event.venue_id,
          reason:
            actionError instanceof APIError
              ? `http_${actionError.status}`
              : 'unable_to_update_rsvp',
        });
        if (!handleReadOnlyMarketError(actionError)) {
          setError(getAPIErrorRecoveryCopy(actionError, 'rsvp', 'Unable to update RSVP'));
        }
      }
    },
    [confirmGoingMutation, defaultRsvpPublic, event, rsvpVisibility, session, updateProfileMutation]
  );

  async function confirmGoing() {
    await submitGoingAfter(closeRsvpSheet);
  }

  async function confirmPostTicketGoing() {
    if (!session) {
      closePostTicketPrompt();
      showGuestWritePrompt('rsvp', {
        next: eventPath ?? undefined,
        onSignIn: () => event && queuePendingRsvp(event.id),
      });
      return;
    }
    if (!(await ensureWritableMarket())) {
      return;
    }

    await submitGoingAfter(closePostTicketPrompt, 'after');
  }

  React.useEffect(() => {
    if (!event || !session || !consumePendingRsvp(event.id)) {
      return;
    }

    const resumeRsvp = async () => {
      if (await ensureWritableMarket()) {
        await submitGoingAfter(() => undefined);
      }
    };
    resumeRsvp().catch(() => undefined);
  }, [ensureWritableMarket, event, session, submitGoingAfter]);

  const toggleSaved = React.useCallback(async () => {
    if (!event) {
      return;
    }
    if (isReadOnlyEvent(event)) {
      setError(
        isCancelledEvent(event) ? 'This event was canceled.' : 'This event has already ended.'
      );
      return;
    }
    if (!session) {
      showGuestWritePrompt('save', {
        message: 'Sign in to save this event.',
        next: eventPath ?? undefined,
      });
      return;
    }

    setError(null);

    try {
      await toggleSavedMutation.mutateAsync({ event });
      if (event.is_saved) {
        trackEventUnsaved({
          eventID: event.id,
          venueID: event.venue_id,
          venueName: event.venue_name,
          surface: Platform.OS === 'web' ? 'web' : 'detail',
        });
      } else {
        trackEventSaved({
          eventID: event.id,
          venueID: event.venue_id,
          venueName: event.venue_name,
          surface: Platform.OS === 'web' ? 'web' : 'detail',
        });
      }
    } catch (actionError) {
      if (!handleReadOnlyMarketError(actionError)) {
        setError(getAPIErrorRecoveryCopy(actionError, 'save', 'Unable to update saved event'));
      }
    }
  }, [event, eventPath, session, toggleSavedMutation]);

  async function removeRsvp() {
    if (!event) {
      return;
    }
    if (!session) {
      showGuestWritePrompt('rsvp_cancel', { next: eventPath ?? undefined });
      return;
    }

    setError(null);
    closeRsvpSheet();

    try {
      await removeRsvpMutation.mutateAsync({ eventID: event.id });
    } catch (actionError) {
      trackRsvpFailed({
        eventID: event.id,
        venueID: event.venue_id,
        reason:
          actionError instanceof APIError ? `http_${actionError.status}` : 'unable_to_cancel_rsvp',
      });
      if (!handleReadOnlyMarketError(actionError)) {
        setError(getAPIErrorRecoveryCopy(actionError, 'rsvp', 'Unable to update RSVP'));
      }
    }
  }

  async function submitComment() {
    if (!event || !commentBody.trim()) {
      return;
    }
    if (isReadOnlyEvent(event)) {
      setError(
        isCancelledEvent(event)
          ? 'This event was canceled. Comments are read-only now.'
          : 'This event has ended. Comments are read-only now.'
      );
      return;
    }
    if (!session) {
      showGuestWritePrompt('comment', { next: eventPath ?? undefined });
      return;
    }

    try {
      addSentryActionBreadcrumb({
        screen: 'event_detail',
        action: 'submit_comment',
        data: {
          event_id: event.id,
          venue_id: event.venue_id,
          comment_length: commentBody.trim().length,
        },
      });
      commentInputTracker.submit(commentBody);
      await submitCommentMutation.mutateAsync({
        eventID: event.id,
        body: commentBody.trim(),
      });
      trackCommentSubmitted({ eventID: event.id, venueID: event.venue_id });
      setCommentBody('');
    } catch (submitError) {
      if (!handleReadOnlyMarketError(submitError)) {
        setError(getAPIErrorRecoveryCopy(submitError, 'comment', 'Unable to post comment'));
      }
    }
  }

  async function deleteComment(commentID: string) {
    if (!event) {
      return;
    }
    if (!session) {
      showGuestWritePrompt('delete_comment', { next: eventPath ?? undefined });
      return;
    }
    if (isReadOnlyEvent(event)) {
      setError(
        isCancelledEvent(event)
          ? 'This event was canceled. Comments are read-only now.'
          : 'This event has ended. Comments are read-only now.'
      );
      return;
    }

    try {
      await deleteCommentMutation.mutateAsync({
        eventID: event.id,
        commentID,
      });
    } catch (deleteError) {
      if (!handleReadOnlyMarketError(deleteError)) {
        setError(getAPIErrorRecoveryCopy(deleteError, 'comment', 'Unable to delete comment'));
      }
    }
  }

  async function hideComment(commentID: string) {
    if (!event) return;
    if (!session) {
      showGuestWritePrompt('hide_comment', { next: eventPath ?? undefined });
      return;
    }
    try {
      await hideCommentMutation.mutateAsync({ eventID: event.id, commentID });
    } catch (hideError) {
      if (!handleReadOnlyMarketError(hideError)) {
        setError(getAPIErrorRecoveryCopy(hideError, 'comment', 'Unable to hide comment'));
      }
    }
  }

  async function unhideComment(commentID: string) {
    if (!event) return;
    if (!session) {
      showGuestWritePrompt('unhide_comment', { next: eventPath ?? undefined });
      return;
    }
    try {
      await unhideCommentMutation.mutateAsync({ eventID: event.id, commentID });
    } catch (unhideError) {
      if (!handleReadOnlyMarketError(unhideError)) {
        setError(getAPIErrorRecoveryCopy(unhideError, 'comment', 'Unable to unhide comment'));
      }
    }
  }

  const handleBack = React.useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/(tabs)/events');
  }, []);

  const openVenuePage = React.useCallback(() => {
    if (!event?.venue_id) {
      return;
    }

    router.navigate(getVenueHref(event.venue_id, { source: 'event_detail' }));
  }, [event?.venue_id]);

  const handleOpenDirections = React.useCallback(() => {
    if (!event || !directionsQuery) {
      return;
    }
    trackEventLinkTapped({
      eventID: event.id,
      venueID: event.venue_id,
      linkType: 'directions',
      surface: eventDetailAnalyticsSurface,
    });
    const query = encodeURIComponent(directionsQuery);
    if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: ['Cancel', 'Apple Maps', 'Google Maps'],
          cancelButtonIndex: 0,
        },
        (idx) => {
          if (idx === 1) {
            Linking.openURL(`https://maps.apple.com/?q=${query}`).catch(() => undefined);
          } else if (idx === 2) {
            Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${query}`).catch(
              () => undefined
            );
          }
        }
      );
      return;
    }
    Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${query}`).catch(
      () => undefined
    );
  }, [directionsQuery, event, eventDetailAnalyticsSurface]);

  const handleAddToCalendar = React.useCallback(() => {
    if (!event) {
      return;
    }
    const calendarURL = buildGoogleCalendarURL(event);
    trackCalendarExportTapped({
      eventID: event.id,
      venueID: event.venue_id,
    }).catch(() => undefined);
    WebBrowser.openBrowserAsync(calendarURL).catch(() => {
      Linking.openURL(calendarURL).catch(() => undefined);
    });
  }, [event]);

  const openHostProfile = React.useCallback(() => {
    if (!event) {
      return;
    }
    if (event.host.is_venue && event.venue_id) {
      openVenuePage();
      return;
    }
    router.navigate(
      getProfileHref(event.host.username, {
        displayName: event.host.display_name,
        bio: event.host.bio || '',
        isFollowing: String(event.host.is_following),
      })
    );
  }, [event, openVenuePage]);

  const confirmCancelEvent = React.useCallback(async () => {
    if (!event || !session || (!isHost && !eventManagementContext)) {
      return;
    }
    if (!(await ensureWritableMarket())) {
      return;
    }

    setError(null);
    try {
      await cancelEventMutation.mutateAsync({
        eventID: event.id,
        venueID: event.venue_id,
        message: cancelMessage,
      });
      setIsCancelModalVisible(false);
      setCancelMessage('');
      await detailQuery.refetch();
    } catch (cancelError) {
      if (!handleReadOnlyMarketError(cancelError)) {
        setError(getAPIErrorRecoveryCopy(cancelError, 'event', 'Unable to cancel event'));
      }
    }
  }, [
    cancelEventMutation,
    cancelMessage,
    detailQuery,
    ensureWritableMarket,
    event,
    eventManagementContext,
    isHost,
    session,
  ]);

  const attendees = React.useMemo<RadiousEventDetail['attendee_preview']>(
    () => (event ? getUniqueEventAttendees(event.attendee_preview) : []),
    [event]
  );
  const totalAttendeeCount = React.useMemo(
    () => Math.max(event?.rsvp_count ?? 0, attendees.length),
    [attendees.length, event?.rsvp_count]
  );
  const isDetailHappeningNow = event ? isDetailEventHappeningNow(event) : false;
  const detailStartsSoonHours = event ? getDetailEventHoursUntilStart(event) : 0;
  const showDetailStartsSoon = event
    ? !isDetailHappeningNow &&
      !hasDetailEventEnded(event) &&
      detailStartsSoonHours > 0 &&
      detailStartsSoonHours <= 3
    : false;
  const resolvedEntryDetails = event ? resolveEventEntryDetails(event) : null;
  const dateEntryLabel = resolvedEntryDetails?.entryType
    ? getEntryTypeBadgeLabel(
        resolvedEntryDetails.entryType,
        resolvedEntryDetails.coverPrice,
        resolvedEntryDetails.coverNotes
      )
    : '';
  const shouldShowEntryBlock = Boolean(
    event &&
    resolvedEntryDetails?.entryType &&
    (resolvedEntryDetails.entryType === EntryTypeCover ||
      resolvedEntryDetails.entryType === EntryTypeTicketed ||
      resolvedEntryDetails.entryType === EntryTypeRSVP ||
      resolvedEntryDetails.entryType === EntryTypeFreeRSVP)
  );
  const entryTypeLabel = resolvedEntryDetails?.entryType
    ? getEntryTypeLabel(resolvedEntryDetails.entryType)
    : '';
  const entryPriceLabel = resolvedEntryDetails?.entryType
    ? getEntryPriceValue(resolvedEntryDetails.entryType, resolvedEntryDetails.coverPrice)
    : '';
  const showPrimaryTicketLink = shouldShowPrimaryTicketCTA(
    resolvedEntryDetails?.entryType,
    event?.ticket_url
  );
  const timingStripItems = React.useMemo(() => {
    if (!event) {
      return [];
    }
    const items = [
      event.doors_at ? { label: 'Doors', value: formatEventTime(event.doors_at) } : null,
      event.starts_at ? { label: 'Starts', value: formatEventTime(event.starts_at) } : null,
      event.last_entry_time
        ? { label: 'Last entry', value: formatEventTime(event.last_entry_time) }
        : null,
      event.ends_at ? { label: 'Ends', value: formatEventTime(event.ends_at) } : null,
    ].filter((item): item is { label: string; value: string } => Boolean(item));
    return items.length >= 2 ? items : [];
  }, [event]);
  const entryPillBg = (() => {
    const t = resolvedEntryDetails?.entryType;
    if (!t || t === EntryTypeFree) return 'rgba(34,197,94,0.12)';
    if (t === EntryTypeCover) return 'rgba(139,92,246,0.12)';
    if (t === EntryTypeTicketed) return 'rgba(245,158,11,0.12)';
    if (t === EntryTypeRSVP || t === EntryTypeFreeRSVP) return 'rgba(236,72,153,0.12)';
    return DESIGN_TOKENS.colors.surfaceRaised;
  })();
  const entryPillText = (() => {
    const t = resolvedEntryDetails?.entryType;
    if (!t || t === EntryTypeFree) return '#4ade80';
    if (t === EntryTypeCover) return '#a78bfa';
    if (t === EntryTypeTicketed) return '#fbbf24';
    if (t === EntryTypeRSVP || t === EntryTypeFreeRSVP) return '#f472b6';
    return DESIGN_TOKENS.colors.text;
  })();
  const entryPillBorder = (() => {
    const t = resolvedEntryDetails?.entryType;
    if (!t || t === EntryTypeFree) return 'rgba(34,197,94,0.25)';
    if (t === EntryTypeCover) return 'rgba(139,92,246,0.25)';
    if (t === EntryTypeTicketed) return 'rgba(245,158,11,0.25)';
    if (t === EntryTypeRSVP || t === EntryTypeFreeRSVP) return 'rgba(236,72,153,0.25)';
    return DESIGN_TOKENS.colors.borderStrong;
  })();
  const ageLabel = event?.age_restriction ? formatEventAgeRestriction(event.age_restriction) : '';
  const attendeeMeta = [
    totalAttendeeCount >= 1 && ageLabel.toLowerCase() === 'all ages' ? 'all ages' : null,
    event && event.capacity > 0 ? `${event.capacity} cap` : null,
  ].filter(Boolean);
  const shouldShowAttendeeMetaRow = Boolean(
    event && (totalAttendeeCount >= 1 || event.capacity > 0)
  );
  const expandedDetailRows = React.useMemo(() => {
    if (!event) {
      return [];
    }
    const rows: {
      icon: React.ComponentProps<typeof Icon>['as'];
      value: string;
      subtitle?: string;
      tone?: 'default' | 'weather' | 'link';
      onPress?: () => void;
    }[] = [];
    const eventURL = normalizeExternalHTTPURL(event.event_url);

    if (event.dress_code?.trim()) {
      rows.push({ icon: ShirtIcon, value: stripTrailingSentencePeriod(event.dress_code) });
    }
    if (event.parking_info?.trim()) {
      rows.push({ icon: CarIcon, value: stripTrailingSentencePeriod(event.parking_info) });
    }
    if (event.weather_dependent || event.rain_plan?.trim()) {
      rows.push({
        icon: CloudRainIcon,
        value: 'Weather dependent',
        subtitle: event.rain_plan?.trim() || undefined,
        tone: 'weather',
      });
    }
    if (event.presented_by?.trim()) {
      rows.push({ icon: UserCircleIcon, value: stripTrailingSentencePeriod(event.presented_by) });
    }
    if (eventURL) {
      rows.push({
        icon: ExternalLinkIcon,
        value: 'More info',
        tone: 'link',
        onPress: () => {
          trackEventLinkTapped({
            eventID: event.id,
            venueID: event.venue_id,
            linkType: 'website',
            surface: eventDetailAnalyticsSurface,
          });
          WebBrowser.openBrowserAsync(eventURL).catch(() => {
            Linking.openURL(eventURL).catch(() => undefined);
          });
        },
      });
    }

    return rows;
  }, [event, eventDetailAnalyticsSurface]);
  const hasOnlyWeatherDetails = Boolean(
    event &&
    expandedDetailRows.length > 0 &&
    !event.dress_code?.trim() &&
    !event.parking_info?.trim() &&
    !event.presented_by?.trim() &&
    !event.event_url?.trim() &&
    (event.weather_dependent || event.rain_plan?.trim())
  );
  const hasOnlyDressCodeDetails = Boolean(
    event &&
    expandedDetailRows.length > 0 &&
    event.dress_code?.trim() &&
    !event.parking_info?.trim() &&
    !event.weather_dependent &&
    !event.rain_plan?.trim() &&
    !event.presented_by?.trim() &&
    !event.event_url?.trim()
  );
  const expandedDetailsLabel = hasOnlyWeatherDetails
    ? 'Weather & more details'
    : hasOnlyDressCodeDetails
      ? 'Dress code & more details'
      : 'Dress code, parking & more';
  const genericDressCode = ['casual', 'none', 'n/a', 'na', 'come as you are'];
  const shouldAutoExpandDetails = Boolean(
    event &&
    ((event.dress_code?.trim() &&
      !genericDressCode.includes(event.dress_code.trim().toLowerCase())) ||
      event.weather_dependent ||
      event.rain_plan?.trim())
  );
  const previousDetailsEventIDRef = React.useRef<string | null>(null);
  const [detailsExpandedOverride, setDetailsExpandedOverride] = React.useState<boolean | null>(
    null
  );
  const detailsExpanded = detailsExpandedOverride ?? shouldAutoExpandDetails;
  const detailsLayoutTransition = React.useMemo(
    () => Platform.select({ native: LinearTransition.duration(180) }),
    []
  );
  const detailsEnterAnimation = React.useMemo(
    () => Platform.select({ native: FadeIn.duration(120) }),
    []
  );
  const detailsExitAnimation = React.useMemo(
    () => Platform.select({ native: FadeOut.duration(90) }),
    []
  );
  const expandedDetailsAnimation = React.useRef(new Animated.Value(0)).current;
  const expandedDetailsChevronRotation = expandedDetailsAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  React.useEffect(() => {
    if (!event?.id || previousDetailsEventIDRef.current === event.id) {
      return;
    }
    previousDetailsEventIDRef.current = event.id;
    setDetailsExpandedOverride(null);
  }, [event?.id]);

  React.useEffect(() => {
    Animated.timing(expandedDetailsAnimation, {
      toValue: detailsExpanded ? 1 : 0,
      duration: 180,
      useNativeDriver: true,
    }).start();
  }, [detailsExpanded, expandedDetailsAnimation]);
  const cancelled = event ? isCancelledEvent(event) : false;
  const archived = event ? isArchivedEvent(event) : false;
  const ended = event ? hasDetailEventEnded(event) : false;
  const readOnly = event ? isReadOnlyEvent(event) : false;
  const canManageViewedEvent = Boolean(!readOnly && (isHost || eventManagementContext));
  const isDesktopWeb = Platform.OS === 'web' && windowWidth >= 768;
  const isMobileWeb = Platform.OS === 'web' && windowWidth < 768;
  const showDirectEntryDownloadBanner =
    isMobileWeb && !session && !readOnly && !isDirectEntryDownloadBannerDismissed;
  const commentComposerBottom = session ? 120 : 28;
  const isWeb = Platform.OS === 'web';
  const detailColumnWidth = isWeb
    ? isDesktopWeb
      ? 900
      : Math.max(320, Math.min(windowWidth - 24, 860))
    : windowWidth;
  const venueCategoryConfig = getVenueCategoryConfig(resolveVenueCategory(venueDetail?.category));
  const stickyBarHeight = (insets.bottom || 16) + 56;
  const nativeCommentComposerBottomPadding =
    !isWeb && isCommentComposerActive ? Math.max(keyboardHeight + 124, stickyBarHeight + 120) : 0;
  const detailContentBottomPadding = isDesktopWeb
    ? 100
    : Math.max(
        commentComposerBottom + (isWeb ? 24 : 0),
        stickyBarHeight + 24,
        nativeCommentComposerBottomPadding
      );
  const handleEditEvent = React.useCallback(() => {
    if (!event) {
      return;
    }
    if (eventManagementContext) {
      void setBusinessContext(
        eventManagementContext.organizationID,
        eventManagementContext.venueID
      ).finally(() => {
        router.push(`/events/${event.id}/edit`);
      });
      return;
    }
    router.push(`/events/${event.id}/edit`);
  }, [event, eventManagementContext, setBusinessContext]);
  const handleOpenPoster = React.useCallback(
    (index: number) => {
      if (!event) {
        return;
      }
      trackEventImageViewed(event.id, event.venue_id);
      setPosterViewerInitialIndex(index);
      setIsPosterViewerVisible(true);
    },
    [event]
  );
  const handleShareEvent = React.useCallback(() => {
    if (!event) {
      return;
    }
    const url = buildEventLink(event.id, event.title, { source: 'share_link' });
    const message = [
      event.title,
      `${event.venue_name} · ${formatEventDayLabel(event.starts_at)}`,
      '',
      `On Radious: ${url}`,
    ].join('\n');
    shareLink({
      message,
      onCopyFallback: () => {
        trackEventShare({
          eventID: event.id,
          surface: eventDetailAnalyticsSurface,
          venueID: event.venue_id,
          shareMethod: 'copy_link',
        });
      },
      title: event.title,
      url,
    })
      .then((result) => {
        if (result !== 'shared') {
          return;
        }

        trackEventShare({
          eventID: event.id,
          surface: eventDetailAnalyticsSurface,
          venueID: event.venue_id,
          shareMethod: 'native_share',
        });
      })
      .catch(() => undefined);
  }, [event, eventDetailAnalyticsSurface]);
  const handleCopyEventLink = React.useCallback(() => {
    if (!event) {
      return;
    }
    Clipboard.setStringAsync(buildEventLink(event.id, event.title, { source: 'share_link' }))
      .then(() => {
        trackEventShare({
          eventID: event.id,
          surface: eventDetailAnalyticsSurface,
          venueID: event.venue_id,
          shareMethod: 'copy_link',
        });
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  }, [event, eventDetailAnalyticsSurface]);
  const handleReportEvent = React.useCallback(() => {
    if (!event) {
      return;
    }
    openReportSheet({ type: 'event', id: event.id, title: event.title });
  }, [event, openReportSheet]);
  const handlePreviewSkeleton = React.useCallback(() => {
    router.push('/events/skeleton');
  }, []);
  const openHeroOverflowSheet = React.useCallback(() => {
    const presentSheet = () => {
      overflowSheetRef.current?.present({ index: 0 });
    };
    requestAnimationFrame(presentSheet);
    setTimeout(presentSheet, 120);
  }, []);
  const runHeroOverflowAction = React.useCallback((action: () => void) => {
    overflowSheetRef.current?.dismiss();
    requestAnimationFrame(action);
  }, []);
  const handleOpenCancelModal = React.useCallback(() => {
    setIsCancelModalVisible(true);
  }, []);
  const handleBrowseFeed = React.useCallback(() => {
    router.push('/(tabs)/events');
  }, []);
  const handleWebSignIn = React.useCallback(() => {
    router.push({
      pathname: '/sign-in',
      params: eventPath ? { next: eventPath } : undefined,
    });
  }, [eventPath]);
  const handleToggleSavedAction = React.useCallback(() => {
    toggleSaved().catch(() => undefined);
  }, [toggleSaved]);

  if (shouldForceSkeleton || (!event && isLoading)) {
    return <EventDetailLoadingSkeleton />;
  }

  if (!event) {
    return (
      <SafeAreaView className="flex-1" style={{ backgroundColor: DESIGN_TOKENS.colors.canvas }}>
        {isWeb ? null : <SceneBackground />}
        <UnavailableState
          eyebrow="Event Link"
          icon={CalendarDaysIcon}
          title="This event is no longer live"
          body="It may have been pulled, wrapped up, or shared from an old link. We will get you back to what is happening now."
          detail={error ?? 'This event could not be loaded right now.'}
          primaryActionLabel="Browse events"
          onPrimaryAction={() => router.replace('/(tabs)/events')}
          secondaryActionLabel="Open map"
          onSecondaryAction={() => router.replace('/(tabs)/map')}
        />
      </SafeAreaView>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <SafeAreaView className="flex-1" style={{ backgroundColor: DESIGN_TOKENS.colors.canvas }}>
        {isWeb ? null : <SceneBackground />}
        {Platform.OS === 'web' ? <AppTopBar pulseBadgeLabel={null} /> : null}
        {showDirectEntryDownloadBanner ? (
          <DirectEntryDownloadBanner
            detailColumnWidth={detailColumnWidth}
            isSharedLinkEntry={isSharedLinkEntry}
            onDismiss={dismissDirectEntryDownloadBanner}
            onDownload={openAppStore}
            onSignIn={handleWebSignIn}
          />
        ) : !session && Platform.OS === 'web' && !readOnly ? (
          <SignInBanner detailColumnWidth={detailColumnWidth} onSignIn={handleWebSignIn} />
        ) : null}
        <KeyboardAvoidingView className="flex-1" behavior={undefined}>
          <View className="flex-1">
            <ScrollView
              ref={scrollViewRef}
              contentContainerStyle={{
                paddingBottom: detailContentBottomPadding,
              }}
              scrollEventThrottle={16}
              showsVerticalScrollIndicator={false}>
              <View
                style={{
                  width: detailColumnWidth,
                  alignSelf: 'center',
                  paddingHorizontal: isDesktopWeb ? 32 : 0,
                  paddingTop: isDesktopWeb ? 24 : isWeb ? 16 : 0,
                }}>
                {isDesktopWeb ? (
                  <DesktopWebEventHero
                    event={event}
                    posterImages={eventPosterImages}
                    canManage={canManageViewedEvent}
                    cancelled={cancelled}
                    archived={archived}
                    ended={ended}
                    isBusy={cancelEventMutation.isPending || isRsvpMutationPending}
                    stickyLabel={getStickyBarLabel(event)}
                    isDetailHappeningNow={isDetailHappeningNow}
                    detailStartsSoonHours={detailStartsSoonHours}
                    showDetailStartsSoon={showDetailStartsSoon}
                    dateEntryLabel={dateEntryLabel}
                    venueCategoryConfig={venueCategoryConfig}
                    venueLogoURL={venueDetail?.logo_url?.trim() || null}
                    resolvedEntryDetails={resolvedEntryDetails}
                    shouldShowEntryBlock={shouldShowEntryBlock}
                    entryPriceLabel={entryPriceLabel}
                    entryTypeLabel={entryTypeLabel}
                    showPrimaryTicketLink={showPrimaryTicketLink}
                    timingStripItems={timingStripItems}
                    attributes={event.attributes}
                    attendees={attendees}
                    totalAttendeeCount={totalAttendeeCount}
                    userOrigin={userOrigin}
                    onBack={handleBack}
                    onShare={handleShareEvent}
                    onCopyLink={handleCopyEventLink}
                    onReport={handleReportEvent}
                    onOpenPoster={handleOpenPoster}
                    onOpenVenuePage={openVenuePage}
                    onOpenDirections={handleOpenDirections}
                    onOpenTicket={() => {
                      const ticketURL = event.ticket_url?.trim();
                      if (!ticketURL) {
                        return;
                      }
                      trackEventLinkTapped({
                        eventID: event.id,
                        venueID: event.venue_id,
                        linkType: 'tickets',
                        surface: eventDetailAnalyticsSurface,
                      });
                      openTicketURL(event, ticketURL).catch(() => undefined);
                    }}
                    onOpenAttendees={() => setIsAttendeeModalVisible(true)}
                    onPrimaryAction={
                      canManageViewedEvent
                        ? handleEditEvent
                        : cancelled || archived || ended
                          ? handleBrowseFeed
                          : event.viewer_rsvp_status === 'going'
                            ? openManageRsvpSheet
                            : openConfirmRsvpSheet
                    }
                  />
                ) : (
                  <HeroSection
                    event={event}
                    posterImages={eventPosterImages}
                    contentWidth={detailColumnWidth}
                    scrollRef={scrollViewRef}
                    onBack={handleBack}
                    onOpenPoster={handleOpenPoster}
                    onShare={handleShareEvent}
                    onOpenMoreOptions={openHeroOverflowSheet}
                  />
                )}

                <View>
                  {canManageViewedEvent && !cancelled && !(archived || ended) ? (
                    <View className="px-4 pb-2 pt-3">
                      <OwnerStatsCard
                        event={event}
                        isCancelling={cancelEventMutation.isPending}
                        onCancel={handleOpenCancelModal}
                        onEdit={handleEditEvent}
                      />
                    </View>
                  ) : null}

                  {!cancelled && (archived || ended) ? (
                    <View className="px-4 pt-3">
                      <EventEndedBanner event={event} />
                    </View>
                  ) : null}

                  <View className="px-4 pt-2">
                    <View>
                      {!isDesktopWeb ? (
                        <>
                          <View className="flex-row items-center py-3">
                            <View className="min-w-0 flex-1">
                              {isDetailHappeningNow ? (
                                <View className="flex-row items-center gap-1.5">
                                  <View
                                    className="size-[5px] rounded-full"
                                    style={{ backgroundColor: DESIGN_TOKENS.colors.accent }}
                                  />
                                  <Text
                                    className="text-[13px] font-semibold"
                                    style={{ color: DESIGN_TOKENS.colors.accent }}>
                                    Happening now
                                  </Text>
                                </View>
                              ) : (
                                <Text
                                  className="text-[13px] font-semibold text-foreground"
                                  numberOfLines={1}>
                                  {formatEventDayLabel(event.starts_at)}
                                  {formatEventTimeRange(event.starts_at, event.ends_at)
                                    ? ` · ${formatEventTimeRange(event.starts_at, event.ends_at)}`
                                    : ''}
                                </Text>
                              )}
                              {showDetailStartsSoon ? (
                                <Text
                                  className="mt-0.5 text-[12px] font-medium"
                                  style={{ color: DESIGN_TOKENS.colors.accent }}>
                                  Starts in {detailStartsSoonHours}h
                                </Text>
                              ) : null}
                            </View>
                            {dateEntryLabel ? (
                              <View
                                className="ml-2 shrink-0 rounded-full px-3 py-1"
                                style={{
                                  backgroundColor: entryPillBg,
                                  borderWidth: 1,
                                  borderColor: entryPillBorder,
                                }}>
                                <Text
                                  className="text-[11px] font-semibold"
                                  style={{ color: entryPillText }}>
                                  {dateEntryLabel}
                                </Text>
                              </View>
                            ) : null}
                          </View>

                          <View
                            className="flex-row items-start gap-2.5 py-3"
                            style={{
                              borderTopWidth: StyleSheet.hairlineWidth,
                              borderTopColor: DESIGN_TOKENS.colors.borderStrong,
                            }}>
                            {event.venue_id ? (
                              <Pressable className="relative mt-0.5" onPress={openVenuePage}>
                                <View
                                  className="size-8 items-center justify-center overflow-hidden rounded-full border"
                                  style={{
                                    borderColor: `#${venueCategoryConfig.color}`,
                                    backgroundColor: `#${venueCategoryConfig.bgColor}`,
                                  }}>
                                  {venueDetail?.logo_url?.trim() ? (
                                    <Image
                                      source={{ uri: venueDetail.logo_url.trim() }}
                                      style={{ width: 32, height: 32, borderRadius: 16 }}
                                      contentFit="cover"
                                      cachePolicy="memory-disk"
                                      transition={200}
                                    />
                                  ) : (
                                    <Icon
                                      as={venueCategoryConfig.icon}
                                      size={14}
                                      color={`#${venueCategoryConfig.color}`}
                                    />
                                  )}
                                </View>
                                {event.venue_claimed ? (
                                  <View
                                    className="absolute bottom-[-2px] right-[-2px] size-[11px] items-center justify-center rounded-full"
                                    style={{
                                      backgroundColor: DESIGN_TOKENS.colors.accent,
                                      borderWidth: 1.5,
                                      borderColor: DESIGN_TOKENS.colors.canvas,
                                    }}>
                                    <Icon
                                      as={CheckIcon}
                                      size={7}
                                      color={DESIGN_TOKENS.colors.textInverse}
                                    />
                                  </View>
                                ) : null}
                              </Pressable>
                            ) : null}

                            <View className="min-w-0 flex-1 gap-0.5">
                              <Pressable
                                className="min-w-0 flex-row items-center gap-1.5"
                                disabled={!event.venue_id}
                                onPress={openVenuePage}>
                                <Text
                                  className="shrink text-[14px] font-semibold"
                                  style={[{ color: DESIGN_TOKENS.colors.text }, CONTAIN_TEXT_STYLE]}
                                  numberOfLines={1}>
                                  {event.venue_name}
                                </Text>
                                {event.distance_miles != null ? (
                                  <Text
                                    className="shrink-0 text-[13px]"
                                    style={{ color: DESIGN_TOKENS.colors.textMuted }}
                                    numberOfLines={1}>
                                    ·{' '}
                                    {formatApproximateDistanceLabel(
                                      event.distance_miles,
                                      userOrigin
                                    )}
                                  </Text>
                                ) : null}
                              </Pressable>
                              {directionsQuery ? (
                                <Pressable
                                  className="flex-row flex-wrap items-center gap-1"
                                  onPress={handleOpenDirections}>
                                  {event.address ? (
                                    <Text
                                      className="text-[12px]"
                                      style={[
                                        { color: DESIGN_TOKENS.colors.textMuted },
                                        CONTAIN_TEXT_STYLE,
                                      ]}
                                      numberOfLines={2}>
                                      {event.address} ·{' '}
                                    </Text>
                                  ) : null}
                                  <Text
                                    className="text-[12px]"
                                    style={{ color: DESIGN_TOKENS.colors.accent }}>
                                    Directions →
                                  </Text>
                                </Pressable>
                              ) : null}
                            </View>
                          </View>

                          {cancelled ? (
                            <CancelledEventBanner
                              event={event}
                              onOpenVenueProfile={
                                event.venue_id
                                  ? openVenuePage
                                  : event.host.is_venue
                                    ? openHostProfile
                                    : undefined
                              }
                              onBrowseFeed={handleBrowseFeed}
                            />
                          ) : null}

                          {shouldShowEntryBlock ? (
                            <View
                              className="flex-row gap-3 py-3"
                              style={{
                                borderTopWidth: StyleSheet.hairlineWidth,
                                borderTopColor: DESIGN_TOKENS.colors.borderStrong,
                              }}>
                              <View className="size-[34px] items-center justify-center rounded-full bg-primary/15">
                                <Icon as={TicketIcon} size={16} className="text-primary" />
                              </View>
                              <View className="min-w-0 flex-1 gap-1">
                                <View className="flex-row flex-wrap items-center gap-1.5">
                                  {entryPriceLabel ? (
                                    <Text className="text-[14px] font-semibold text-primary">
                                      {entryPriceLabel}
                                    </Text>
                                  ) : null}
                                  {entryTypeLabel ? (
                                    <Text className="text-[14px] font-medium text-foreground">
                                      {entryTypeLabel}
                                    </Text>
                                  ) : null}
                                </View>
                                {resolvedEntryDetails?.coverNotes?.trim() ? (
                                  <Text
                                    className="text-[12px] leading-4 text-muted-foreground"
                                    numberOfLines={Platform.OS === 'web' ? undefined : 3}
                                    ellipsizeMode="clip"
                                    style={[CONTAIN_TEXT_STYLE, WEB_ENTRY_NOTES_CLIP_STYLE]}>
                                    {resolvedEntryDetails.coverNotes.trim()}
                                  </Text>
                                ) : null}
                                {showPrimaryTicketLink ? (
                                  <Pressable
                                    className="mt-1 self-start"
                                    onPress={() => {
                                      const ticketURL = event.ticket_url?.trim();
                                      if (!ticketURL) {
                                        return;
                                      }
                                      trackEventLinkTapped({
                                        eventID: event.id,
                                        venueID: event.venue_id,
                                        linkType: 'tickets',
                                        surface: eventDetailAnalyticsSurface,
                                      });
                                      openTicketURL(event, ticketURL).catch(() => undefined);
                                    }}>
                                    <Text className="text-[13px] font-semibold text-primary">
                                      Buy tickets
                                    </Text>
                                  </Pressable>
                                ) : null}
                              </View>
                            </View>
                          ) : null}

                          {timingStripItems.length > 0 ? (
                            <View
                              className="flex-row py-3"
                              style={{
                                borderTopWidth: StyleSheet.hairlineWidth,
                                borderTopColor: DESIGN_TOKENS.colors.borderStrong,
                              }}>
                              {timingStripItems.slice(0, 4).map((item, index) => (
                                <View
                                  key={item.label}
                                  className="flex-1 px-2"
                                  style={{
                                    borderLeftWidth: index > 0 ? StyleSheet.hairlineWidth : 0,
                                    borderLeftColor: DESIGN_TOKENS.colors.borderStrong,
                                  }}>
                                  <Text className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                                    {item.label}
                                  </Text>
                                  <Text className="mt-0.5 text-[13px] font-medium text-foreground">
                                    {item.value}
                                  </Text>
                                </View>
                              ))}
                            </View>
                          ) : null}

                          {shouldShowAttendeeMetaRow ? (
                            <View
                              className="flex-row flex-wrap items-center gap-1 py-3"
                              style={{
                                borderTopWidth: StyleSheet.hairlineWidth,
                                borderTopColor: DESIGN_TOKENS.colors.borderStrong,
                              }}>
                              {totalAttendeeCount >= 1 ? (
                                <HeroAttendeeSummary
                                  attendees={attendees}
                                  totalCount={totalAttendeeCount}
                                  canOpen={canManageViewedEvent && attendees.length > 0}
                                  onPress={() => setIsAttendeeModalVisible(true)}
                                  avatarSize={22}
                                  textColor={DESIGN_TOKENS.colors.textMuted}
                                />
                              ) : null}
                              {attendeeMeta.length > 0 ? (
                                <Text className="text-[13px] text-muted-foreground">
                                  {totalAttendeeCount >= 1
                                    ? `· ${attendeeMeta.join(' · ')}`
                                    : attendeeMeta.join(' · ')}
                                </Text>
                              ) : null}
                            </View>
                          ) : null}
                        </>
                      ) : null}

                      {event.description || event.short_description ? (
                        <View
                          className="py-3"
                          style={{
                            borderTopWidth: StyleSheet.hairlineWidth,
                            borderTopColor: DESIGN_TOKENS.colors.borderStrong,
                          }}>
                          <Text
                            className="text-[16px] font-normal leading-relaxed text-foreground"
                            style={[CONTAIN_TEXT_STYLE, WEB_DESCRIPTION_CLIP_STYLE]}>
                            {event.description || event.short_description}
                          </Text>
                        </View>
                      ) : null}

                      {!isDesktopWeb && event.tags.length > 0 ? (
                        <View
                          className="flex-row flex-wrap gap-2 py-3"
                          style={{
                            borderTopWidth: StyleSheet.hairlineWidth,
                            borderTopColor: DESIGN_TOKENS.colors.borderStrong,
                          }}>
                          {event.tags.map((tag) => (
                            <View
                              key={tag}
                              className="flex-row items-center gap-2 rounded-full border px-3 py-1.5"
                              style={{
                                borderColor: DESIGN_TOKENS.colors.borderStrong,
                                backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
                              }}>
                              <EventTagDot value={tag} size={8} />
                              <Text variant="small" className="text-muted-foreground">
                                {getEventTagLabel(tag)}
                              </Text>
                            </View>
                          ))}
                        </View>
                      ) : null}

                      {!isDesktopWeb && event.attributes.length > 0 ? (
                        <View
                          className="pb-3 pt-1"
                          style={{
                            borderTopWidth: event.tags.length > 0 ? 0 : StyleSheet.hairlineWidth,
                            borderTopColor: DESIGN_TOKENS.colors.borderStrong,
                          }}>
                          <View className="flex-row flex-wrap gap-2">
                            {event.attributes.map((attribute) => (
                              <View
                                key={attribute}
                                className="rounded-full border border-border-strong bg-surface-raised px-3 py-1.5">
                                <Text variant="small" className="text-muted-foreground">
                                  {getEventAttributeLabel(attribute)}
                                </Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      ) : null}

                      {expandedDetailRows.length > 0 ? (
                        <Reanimated.View
                          className="py-3"
                          layout={detailsLayoutTransition}
                          style={{
                            borderTopWidth: StyleSheet.hairlineWidth,
                            borderTopColor: DESIGN_TOKENS.colors.borderStrong,
                          }}>
                          <Pressable
                            className="flex-row items-center justify-between gap-3 py-1"
                            onPress={() => setDetailsExpandedOverride(!detailsExpanded)}>
                            <Text className="text-[13px] font-medium text-muted-foreground">
                              {expandedDetailsLabel}
                            </Text>
                            <Animated.View
                              style={{ transform: [{ rotate: expandedDetailsChevronRotation }] }}>
                              <Icon
                                as={ChevronDownIcon}
                                size={16}
                                className="text-muted-foreground"
                              />
                            </Animated.View>
                          </Pressable>
                          {detailsExpanded ? (
                            <Reanimated.View
                              className="pt-2"
                              entering={detailsEnterAnimation}
                              exiting={detailsExitAnimation}
                              layout={detailsLayoutTransition}>
                              {expandedDetailRows.map((row) => {
                                const RowContainer = row.onPress ? Pressable : View;
                                const isWeather = row.tone === 'weather';
                                const isLink = row.tone === 'link';

                                return (
                                  <RowContainer
                                    key={`${row.value}:${row.subtitle ?? row.tone ?? 'default'}`}
                                    className="flex-row items-start gap-3 py-2"
                                    onPress={row.onPress}>
                                    <View className="w-5 items-start pt-[2px]">
                                      <Icon
                                        as={row.icon}
                                        size={16}
                                        color={isWeather ? '#85b7eb' : undefined}
                                        className={
                                          isWeather
                                            ? undefined
                                            : isLink
                                              ? 'text-primary'
                                              : 'text-muted-foreground'
                                        }
                                      />
                                    </View>
                                    <View className="min-w-0 flex-1">
                                      <Text
                                        className={cn(
                                          'text-[14px] font-medium leading-5',
                                          isLink
                                            ? 'text-primary'
                                            : isWeather
                                              ? ''
                                              : 'text-foreground'
                                        )}
                                        style={isWeather ? { color: '#85b7eb' } : undefined}>
                                        {row.value}
                                      </Text>
                                      {row.subtitle ? (
                                        <Text
                                          className="text-[12px] leading-4 text-muted-foreground"
                                          style={
                                            isWeather
                                              ? { color: 'rgba(133,183,235,0.72)' }
                                              : undefined
                                          }>
                                          {row.subtitle}
                                        </Text>
                                      ) : null}
                                    </View>
                                  </RowContainer>
                                );
                              })}
                            </Reanimated.View>
                          ) : null}
                        </Reanimated.View>
                      ) : null}

                      {event.host_is_curator ? (
                        <Pressable
                          className="flex-row items-center gap-[6px]"
                          onPress={openHostProfile}>
                          <Image
                            source={RADIOUS_ICON}
                            style={{ width: 12, height: 12 }}
                            contentFit="cover"
                          />
                          <Text
                            className="text-[11px]"
                            style={{ color: DESIGN_TOKENS.colors.textMuted, opacity: 0.75 }}>
                            Curated by Radious SA
                          </Text>
                        </Pressable>
                      ) : null}
                    </View>
                  </View>
                  <View className="px-4 pt-4">
                    <View className="gap-4">
                      <View
                        className="h-px"
                        style={{ backgroundColor: DESIGN_TOKENS.colors.borderStrong }}
                      />
                      <Text className="text-[13px] font-semibold text-muted-foreground">
                        Comments
                      </Text>

                      <View className="gap-3">
                        {comments.length > 0 ? (
                          comments.map((comment) => (
                            <CommentRow
                              key={comment.id}
                              comment={comment}
                              canDelete={
                                !readOnly &&
                                Boolean(
                                  session &&
                                  (comment.author.user_id === session.me.profile.user_id ||
                                    event.host.user_id === session.me.profile.user_id)
                                )
                              }
                              isAuthenticated={Boolean(session)}
                              onDelete={() => deleteComment(comment.id).catch(() => undefined)}
                              onHide={() => hideComment(comment.id).catch(() => undefined)}
                              onUnhide={() => unhideComment(comment.id).catch(() => undefined)}
                              onReport={() =>
                                openReportSheet({
                                  type: 'comment',
                                  id: comment.id,
                                  preview: comment.body.slice(0, 60),
                                })
                              }
                              onOpenProfile={() =>
                                router.push(
                                  getPublicIdentityHref(comment.author, {
                                    displayName: comment.author.display_name,
                                    bio: comment.author.bio || '',
                                  })
                                )
                              }
                            />
                          ))
                        ) : (
                          <View className="pt-2">
                            <Text variant="small" className="text-muted-foreground">
                              No comments yet.
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>

                    {readOnly ? (
                      <View
                        className="mt-5 rounded-[24px] border p-4"
                        style={{
                          borderColor: DESIGN_TOKENS.colors.borderStrong,
                          backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
                        }}>
                        <Text variant="small" className="text-muted-foreground">
                          {cancelled
                            ? 'This event was canceled. Comments stay visible here, but the conversation is now read-only.'
                            : 'This event has ended. Comments stay visible here as part of the archive.'}
                        </Text>
                      </View>
                    ) : session && isWeb ? (
                      <CommentComposer
                        value={commentBody}
                        onChangeText={(value) => {
                          commentInputTracker.changeText(value);
                          setCommentBody(value);
                        }}
                        onFocus={() => commentInputTracker.focus()}
                        onBlur={() => commentInputTracker.blur(commentBody)}
                        isSubmitting={isSubmittingComment}
                        profileName={session.me.profile.display_name}
                        avatarURL={session.me.profile.avatar_url}
                        onSubmit={() => submitComment().catch(() => undefined)}
                      />
                    ) : session && !isCommentComposerActive ? (
                      <CommentComposerLauncher
                        profileName={session.me.profile.display_name}
                        avatarURL={session.me.profile.avatar_url}
                        onPress={() => setIsCommentComposerActive(true)}
                      />
                    ) : session ? null : (
                      <View className="mt-5">
                        <SignInPrompt
                          compact
                          title="Sign in to join the thread."
                          body={
                            event?.host.is_venue
                              ? 'Comments, RSVPs, and venue follows stay with your Radious identity.'
                              : 'Comments, RSVPs, and follows stay with your Radious identity.'
                          }
                        />
                      </View>
                    )}
                  </View>

                  {error ? (
                    <View className="mt-8">
                      <ErrorState
                        title="Action didn’t stick."
                        body={error}
                        actionLabel="Reload event"
                        onAction={() => detailQuery.refetch().catch(() => undefined)}
                      />
                    </View>
                  ) : null}
                </View>
              </View>
            </ScrollView>

            <LinkCopiedToast visible={copied} />

            {event && session && !readOnly && !isWeb && isCommentComposerActive ? (
              <View
                className="absolute inset-x-0 px-4"
                style={{
                  bottom: Math.max(keyboardHeight - insets.bottom + 8, insets.bottom + 12),
                }}>
                <View className="w-full self-center" style={{ maxWidth: detailColumnWidth }}>
                  <CommentComposer
                    autoFocus
                    className="mt-0"
                    value={commentBody}
                    onChangeText={(value) => {
                      commentInputTracker.changeText(value);
                      setCommentBody(value);
                    }}
                    onFocus={() => commentInputTracker.focus()}
                    onBlur={() => commentInputTracker.blur(commentBody)}
                    isSubmitting={isSubmittingComment}
                    profileName={session.me.profile.display_name}
                    avatarURL={session.me.profile.avatar_url}
                    onSubmit={() => submitComment().catch(() => undefined)}
                  />
                </View>
              </View>
            ) : null}

            {event && !isDesktopWeb && !rsvpSheetMode && !webGuestAction && !isKeyboardVisible ? (
              <StickyActionBar
                bottomInset={insets.bottom || 16}
                event={event}
                mode={
                  canManageViewedEvent
                    ? 'manage'
                    : cancelled || archived || ended
                      ? 'readonly'
                      : 'default'
                }
                isBusy={cancelEventMutation.isPending || isRsvpMutationPending}
                stickyLabel={getStickyBarLabel(event)}
                onBrowseFeed={handleBrowseFeed}
                onCancel={handleOpenCancelModal}
                onEdit={handleEditEvent}
                onOpenConfirmRsvp={openConfirmRsvpSheet}
                onOpenManageRsvp={openManageRsvpSheet}
                onToggleSaved={handleToggleSavedAction}
                maxWidth={isWeb ? detailColumnWidth : undefined}
              />
            ) : null}
          </View>
        </KeyboardAvoidingView>

        {Platform.OS === 'web' && (rsvpSheetMode || webGuestAction) ? (
          <WebEventActionModal onClose={closeRsvpSheet}>
            {webGuestAction ? (
              <WebGuestEventAction
                title={event.title}
                action={webGuestAction}
                onClose={closeRsvpSheet}
                onSignIn={() => {
                  closeRsvpSheet();
                  handleWebSignIn();
                }}
                onGetApp={openAppStore}
              />
            ) : rsvpSheetMode === 'manage' ? (
              <ManageRsvpSheetContent
                title={event.title}
                venueName={event.venue_name}
                startsAt={event.starts_at}
                visibility={rsvpVisibility}
                isBusy={isRsvpMutationPending}
                onChangeVisibility={changeRsvpVisibility}
                onSaveVisibility={() => confirmGoing().catch(() => undefined)}
                onCancelRsvp={() => {
                  closeRsvpSheet();
                  removeRsvp().catch(() => undefined);
                }}
                onClose={closeRsvpSheet}
              />
            ) : (
              <RsvpConfirmSheet
                title={event.title}
                posterURL={event.poster_url}
                posterImage={getCoverEventPosterImage(event)}
                dayLabel={formatEventDayLabel(event.starts_at)}
                distanceLabel={formatApproximateDistanceLabel(event.distance_miles, userOrigin)}
                timeLabel={formatEventTimeRange(event.starts_at, event.ends_at)}
                timeDetail={formatDetailSheetTime(event.starts_at)}
                venueName={event.venue_name}
                venueDetail={event.address || undefined}
                visibility={rsvpVisibility}
                isBusy={isRsvpMutationPending}
                isFull={isDetailEventFull(event)}
                onChangeVisibility={changeRsvpVisibility}
                onConfirm={() => confirmGoing().catch(() => undefined)}
                onClose={closeRsvpSheet}
              />
            )}
          </WebEventActionModal>
        ) : null}

        {Platform.OS === 'web' && reportTarget ? (
          <WebReportModal
            target={reportTarget}
            onReport={submitReport}
            onClose={handleReportDismiss}
          />
        ) : null}

        <PosterViewer
          images={eventPosterImages}
          initialIndex={posterViewerInitialIndex}
          visible={isPosterViewerVisible}
          onClose={() => setIsPosterViewerVisible(false)}
        />

        <AttendeeModal
          visible={isAttendeeModalVisible}
          attendees={attendees}
          totalCount={totalAttendeeCount}
          onClose={() => setIsAttendeeModalVisible(false)}
        />

        <CancelEventModal
          visible={isCancelModalVisible}
          message={cancelMessage}
          isSubmitting={cancelEventMutation.isPending}
          onChangeMessage={setCancelMessage}
          onClose={() => {
            if (cancelEventMutation.isPending) {
              return;
            }
            setIsCancelModalVisible(false);
          }}
          onConfirm={() => confirmCancelEvent().catch(() => undefined)}
        />
      </SafeAreaView>

      {Platform.OS !== 'web' ? (
        <EventDetailOverflowSheet
          ref={overflowSheetRef}
          canManage={canManageViewedEvent}
          canCancel={canManageViewedEvent && !cancelled && !(archived || ended)}
          hasVenuePage={Boolean(event.venue_id)}
          hasDirections={Boolean(directionsQuery)}
          showSkeletonPreview={__DEV__}
          insets={insets}
          renderBackdrop={renderBackdrop}
          onAddToCalendar={() => runHeroOverflowAction(handleAddToCalendar)}
          onOpenVenue={() => runHeroOverflowAction(openVenuePage)}
          onOpenDirections={() => runHeroOverflowAction(handleOpenDirections)}
          onCopyLink={() => runHeroOverflowAction(handleCopyEventLink)}
          onEdit={() => runHeroOverflowAction(handleEditEvent)}
          onCancel={() => runHeroOverflowAction(handleOpenCancelModal)}
          onReport={() => runHeroOverflowAction(handleReportEvent)}
          onPreviewSkeleton={() => runHeroOverflowAction(handlePreviewSkeleton)}
        />
      ) : null}

      {Platform.OS !== 'web' ? (
        <RsvpBottomSheet
          ref={bottomSheetRef}
          rsvpSheetMode={rsvpSheetMode ?? 'confirm'}
          event={event}
          snapPoints={snapPoints}
          insets={insets}
          renderBackdrop={renderBackdrop}
          visibility={rsvpVisibility}
          isRsvpMutationPending={isRsvpMutationPending}
          onDismiss={handleRsvpSheetDismiss}
          onClose={closeRsvpSheet}
          onConfirm={() => confirmGoing().catch(() => undefined)}
          onChangeVisibility={changeRsvpVisibility}
          onCancelRsvp={() => {
            closeRsvpSheet();
            removeRsvp().catch(() => undefined);
          }}
        />
      ) : null}

      {Platform.OS !== 'web' && event ? (
        <BottomSheetModal
          ref={postTicketPromptSheetRef}
          index={0}
          snapPoints={postTicketPromptSnapPoints}
          enablePanDownToClose
          backdropComponent={renderBackdrop}
          backgroundStyle={{
            backgroundColor: DESIGN_TOKENS.colors.canvas,
            borderRadius: 28,
          }}
          handleIndicatorStyle={{
            backgroundColor: 'rgba(255,255,255,0.24)',
            width: 42,
            height: 5,
          }}
          style={{
            marginHorizontal: 16,
          }}>
          <BottomSheetView
            className="px-5 pb-5 pt-2"
            style={{ backgroundColor: DESIGN_TOKENS.colors.canvas }}>
            <Text className="font-display text-[18px] text-foreground">Planning to go?</Text>
            <Text className="mt-2 text-[13px] leading-5 text-white/50">
              Mark yourself as going so friends can see your plans.
            </Text>
            <View className="mt-5 gap-2.5">
              <Pressable
                className={cn(
                  'h-12 w-full items-center justify-center rounded-full bg-primary',
                  isRsvpMutationPending && 'opacity-60'
                )}
                disabled={isRsvpMutationPending}
                onPress={() => confirmPostTicketGoing().catch(() => undefined)}>
                <Text className="text-[15px] font-semibold text-black">I&apos;m Going</Text>
              </Pressable>
              <Pressable
                className="h-12 w-full items-center justify-center rounded-full border border-white/15"
                disabled={isRsvpMutationPending}
                onPress={closePostTicketPrompt}>
                <Text className="text-[15px] font-semibold text-foreground">Not now</Text>
              </Pressable>
            </View>
          </BottomSheetView>
        </BottomSheetModal>
      ) : null}

      {Platform.OS !== 'web' ? (
        <ReportSheet
          ref={reportSheetRef}
          target={reportTarget}
          onReport={submitReport}
          onDismiss={handleReportDismiss}
        />
      ) : null}
    </>
  );
}

function DesktopWebEventHero({
  event,
  posterImages,
  canManage,
  cancelled,
  archived,
  ended,
  isBusy,
  stickyLabel,
  isDetailHappeningNow,
  detailStartsSoonHours,
  showDetailStartsSoon,
  dateEntryLabel,
  venueCategoryConfig,
  venueLogoURL,
  resolvedEntryDetails,
  shouldShowEntryBlock,
  entryPriceLabel,
  entryTypeLabel,
  showPrimaryTicketLink,
  timingStripItems,
  attributes,
  attendees,
  totalAttendeeCount,
  userOrigin,
  onBack,
  onShare,
  onCopyLink,
  onReport,
  onOpenPoster,
  onOpenVenuePage,
  onOpenDirections,
  onOpenTicket,
  onOpenAttendees,
  onPrimaryAction,
}: {
  event: RadiousEventDetail;
  posterImages: FlyerImageSource[];
  canManage: boolean;
  cancelled: boolean;
  archived: boolean;
  ended: boolean;
  isBusy: boolean;
  stickyLabel: string;
  isDetailHappeningNow: boolean;
  detailStartsSoonHours: number;
  showDetailStartsSoon: boolean;
  dateEntryLabel: string;
  venueCategoryConfig: ReturnType<typeof getVenueCategoryConfig>;
  venueLogoURL: string | null;
  resolvedEntryDetails: ReturnType<typeof resolveEventEntryDetails> | null;
  shouldShowEntryBlock: boolean;
  entryPriceLabel: string;
  entryTypeLabel: string;
  showPrimaryTicketLink: boolean;
  timingStripItems: { label: string; value: string }[];
  attributes: string[];
  attendees: RadiousEventDetail['attendee_preview'];
  totalAttendeeCount: number;
  userOrigin: ReturnType<typeof useUserOriginCoords>;
  onBack: () => void;
  onShare: () => void;
  onCopyLink: () => void;
  onReport: () => void;
  onOpenPoster: (index: number) => void;
  onOpenVenuePage: () => void;
  onOpenDirections: () => void;
  onOpenTicket: () => void;
  onOpenAttendees: () => void;
  onPrimaryAction: () => void;
}) {
  const [activePosterIndex, setActivePosterIndex] = React.useState(0);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = React.useState(false);
  const isGoing = event.viewer_rsvp_status === 'going';
  const isFull = isDetailEventFull(event);
  const isReadOnly = cancelled || archived || ended;
  const primaryLabel = canManage ? 'Edit Event' : isReadOnly ? 'Browse tonight' : stickyLabel;
  const primaryDisabled = isBusy || (!canManage && !isReadOnly && isFull);
  const safePosterIndex = Math.min(activePosterIndex, Math.max(posterImages.length - 1, 0));
  const activePoster = posterImages[safePosterIndex] ?? null;

  React.useEffect(() => {
    setActivePosterIndex(0);
  }, [event.id, posterImages.length]);

  return (
    <View className="flex-row items-start pb-6">
      <Pressable
        className="relative basis-[52%] overflow-hidden rounded-[14px] border border-border-strong bg-canvas-inset"
        onPress={() => {
          if (activePoster) {
            onOpenPoster(safePosterIndex);
          }
        }}>
        <View className="aspect-[8/10] w-full overflow-hidden">
          {activePoster ? (
            <FlyerImage image={activePoster} style={{ height: '100%', width: '100%' }} />
          ) : (
            <View className="h-full w-full bg-surface-raised" />
          )}
        </View>
        {posterImages.length > 1 ? (
          <View className="absolute bottom-3 left-0 right-0 flex-row justify-center gap-1.5">
            {posterImages.map((poster, index) => (
              <Pressable
                key={poster.public_url}
                accessibilityRole="button"
                accessibilityLabel={`Show poster ${index + 1}`}
                className={cn(
                  'h-1.5 rounded-full',
                  index === safePosterIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/45'
                )}
                onPress={(pressEvent) => {
                  pressEvent.stopPropagation();
                  setActivePosterIndex(index);
                }}
              />
            ))}
          </View>
        ) : null}
        <View className="absolute inset-x-0 top-0 flex-row items-center justify-between px-3 pt-3">
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Back"
            className="size-9 items-center justify-center rounded-full border border-white/10 bg-black/55"
            onPress={onBack}>
            <Icon as={ArrowLeftIcon} size={16} className="text-white" />
          </Pressable>
          <View className="flex-row gap-2">
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Share event"
              className="size-9 items-center justify-center rounded-full border border-white/10 bg-black/55"
              onPress={onShare}>
              <Icon as={ShareIcon} size={15} className="text-white" />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="More event actions"
              className="size-9 items-center justify-center rounded-full border border-white/10 bg-black/55"
              onPress={(pressEvent) => {
                pressEvent.stopPropagation();
                setIsMoreMenuOpen((value) => !value);
              }}>
              <Icon as={MoreHorizontalIcon} size={17} className="text-white" />
            </Pressable>
          </View>
        </View>
        {isMoreMenuOpen ? (
          <View className="absolute right-3 top-[54px] w-[160px] rounded-[14px] border border-border-strong bg-background p-1 shadow-lg">
            <Pressable
              className="rounded-[11px] px-3 py-2"
              onPress={(pressEvent) => {
                pressEvent.stopPropagation();
                setIsMoreMenuOpen(false);
                onCopyLink();
              }}>
              <Text className="text-[12px] font-semibold text-muted-foreground">Copy link</Text>
            </Pressable>
            <Pressable
              className="rounded-[11px] px-3 py-2"
              onPress={(pressEvent) => {
                pressEvent.stopPropagation();
                setIsMoreMenuOpen(false);
                onReport();
              }}>
              <Text className="text-[12px] font-semibold text-primary">Report event</Text>
            </Pressable>
          </View>
        ) : null}
      </Pressable>

      <View
        className="min-w-0 flex-1 justify-between gap-[14px] pl-8"
        style={{ alignSelf: 'flex-start' }}>
        <View className="gap-[14px]">
          <Text
            className="text-[26px] font-medium leading-[31px] text-foreground"
            numberOfLines={Platform.OS === 'web' ? undefined : 5}
            ellipsizeMode="clip"
            style={[CONTAIN_TEXT_STYLE, WEB_TITLE_CLIP_STYLE]}>
            {event.title}
          </Text>

          <View className="flex-row items-start gap-2.5">
            {event.venue_id ? (
              <Pressable className="relative mt-0.5" onPress={onOpenVenuePage}>
                <View
                  className="size-8 items-center justify-center overflow-hidden rounded-full border"
                  style={{
                    borderColor: `#${venueCategoryConfig.color}`,
                    backgroundColor: `#${venueCategoryConfig.bgColor}`,
                  }}>
                  {venueLogoURL ? (
                    <Image
                      source={{ uri: venueLogoURL }}
                      style={{ width: 32, height: 32, borderRadius: 16 }}
                      contentFit="cover"
                      cachePolicy="memory-disk"
                      transition={160}
                    />
                  ) : (
                    <Icon
                      as={venueCategoryConfig.icon}
                      size={14}
                      color={`#${venueCategoryConfig.color}`}
                    />
                  )}
                </View>
                {event.venue_claimed ? (
                  <View className="border-canvas absolute bottom-[-2px] right-[-2px] size-[11px] items-center justify-center rounded-full border-[1.5px] bg-primary">
                    <Icon as={CheckIcon} size={7} className="text-primary-foreground" />
                  </View>
                ) : null}
              </Pressable>
            ) : null}
            <View className="min-w-0 flex-1 gap-0.5">
              <Pressable
                className="min-w-0 flex-row items-center gap-1.5"
                disabled={!event.venue_id}
                onPress={onOpenVenuePage}>
                <Text
                  className="shrink text-[14px] font-semibold text-foreground"
                  style={CONTAIN_TEXT_STYLE}
                  numberOfLines={1}>
                  {event.venue_name}
                </Text>
                {event.distance_miles != null ? (
                  <Text className="shrink-0 text-[13px] text-muted-foreground" numberOfLines={1}>
                    · {formatApproximateDistanceLabel(event.distance_miles, userOrigin)}
                  </Text>
                ) : null}
              </Pressable>
              {event.address || event.venue_id ? (
                <Pressable
                  className="flex-row flex-wrap items-center gap-1"
                  onPress={onOpenDirections}>
                  {event.address ? (
                    <Text
                      className="text-[12px] text-muted-foreground"
                      numberOfLines={2}
                      style={CONTAIN_TEXT_STYLE}>
                      {event.address} ·{' '}
                    </Text>
                  ) : null}
                  <Text className="text-[12px] text-primary">Directions →</Text>
                </Pressable>
              ) : null}
            </View>
          </View>

          <View className="flex-row flex-wrap items-center gap-2 border-t border-border-strong pt-3">
            {isDetailHappeningNow ? (
              <View className="flex-row items-center gap-1.5 rounded-full border border-primary-edge bg-primary/15 px-3 py-1">
                <View className="size-[5px] rounded-full bg-primary" />
                <Text className="text-[11px] font-semibold text-primary">Happening now</Text>
              </View>
            ) : (
              <View className="rounded-full bg-surface-raised px-3 py-1">
                <Text className="text-[12px] font-semibold text-foreground">
                  {formatEventDayLabel(event.starts_at)} ·{' '}
                  {formatEventTimeRange(event.starts_at, event.ends_at)}
                </Text>
              </View>
            )}
            {showDetailStartsSoon ? (
              <View className="flex-row items-center gap-1.5 rounded-full border border-primary-edge bg-primary/15 px-3 py-1">
                <View className="size-[5px] rounded-full bg-primary" />
                <Text className="text-[11px] font-semibold text-primary">
                  Starts in {detailStartsSoonHours}h
                </Text>
              </View>
            ) : null}
            {dateEntryLabel ? (
              <View className="rounded-full border border-primary/40 bg-primary/15 px-3 py-1">
                <Text className="text-[11px] font-semibold text-primary">{dateEntryLabel}</Text>
              </View>
            ) : null}
          </View>

          {shouldShowEntryBlock ? (
            <View className="flex-row gap-3 border-t border-border-strong pt-3">
              <View className="size-[34px] items-center justify-center rounded-full bg-primary/15">
                <Icon as={TicketIcon} size={16} className="text-primary" />
              </View>
              <View className="min-w-0 flex-1 gap-1">
                <View className="flex-row flex-wrap items-center gap-1.5">
                  {entryPriceLabel ? (
                    <Text className="text-[14px] font-semibold text-primary">
                      {entryPriceLabel}
                    </Text>
                  ) : null}
                  {entryTypeLabel ? (
                    <Text className="text-[14px] font-medium text-foreground">
                      {entryTypeLabel}
                    </Text>
                  ) : null}
                </View>
                {resolvedEntryDetails?.coverNotes?.trim() ? (
                  <Text
                    className="text-[12px] leading-4 text-muted-foreground"
                    numberOfLines={Platform.OS === 'web' ? undefined : 4}
                    ellipsizeMode="tail"
                    style={[CONTAIN_TEXT_STYLE, WEB_ENTRY_NOTES_CLIP_STYLE]}>
                    {resolvedEntryDetails.coverNotes.trim()}
                  </Text>
                ) : null}
                {showPrimaryTicketLink ? (
                  <Pressable className="mt-1 self-start" onPress={onOpenTicket}>
                    <Text className="text-[13px] font-semibold text-primary">Buy tickets</Text>
                  </Pressable>
                ) : null}
              </View>
            </View>
          ) : null}

          {timingStripItems.length > 0 ? (
            <View className="flex-row border-t border-border-strong pt-3">
              {timingStripItems.slice(0, 4).map((item, index) => (
                <View
                  key={item.label}
                  className={cn('flex-1 px-2', index > 0 && 'border-l border-border-strong')}>
                  <Text className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                    {item.label}
                  </Text>
                  <Text className="mt-0.5 text-[13px] font-medium text-foreground">
                    {item.value}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}

          {event.tags.length > 0 ? (
            <View className="flex-row flex-wrap gap-2 border-t border-border-strong pt-3">
              {event.tags.map((tag) => (
                <View
                  key={tag}
                  className="flex-row items-center gap-2 rounded-full border border-border-strong bg-surface-raised px-3 py-1.5">
                  <EventTagDot value={tag} size={8} />
                  <Text variant="small" className="text-muted-foreground">
                    {getEventTagLabel(tag)}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}

          {attributes.length > 0 ? (
            <View className="flex-row flex-wrap gap-2 border-t border-border-strong pt-3">
              {attributes.map((attribute) => (
                <View
                  key={attribute}
                  className="rounded-full border border-border-strong bg-surface-raised px-3 py-1.5">
                  <Text variant="small" className="text-muted-foreground">
                    {getEventAttributeLabel(attribute)}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}
        </View>

        <View className="gap-4 pt-2">
          {event.rsvp_count >= 1 || event.capacity > 0 ? (
            <DesktopWebAttendeePreview
              attendees={attendees}
              capacity={event.capacity}
              goingCount={event.rsvp_count}
              canOpen={canManage && attendees.length > 0}
              onPress={onOpenAttendees}
            />
          ) : null}
          <Pressable
            className={cn(
              'w-full items-center justify-center rounded-full px-4 py-[14px]',
              isGoing
                ? 'border border-primary-edge bg-surface-accent'
                : primaryDisabled
                  ? 'border border-border-strong bg-surface-raised'
                  : 'bg-primary'
            )}
            disabled={primaryDisabled}
            onPress={onPrimaryAction}>
            <Text
              className={cn(
                'text-[15px] font-medium',
                isGoing
                  ? 'text-primary'
                  : primaryDisabled
                    ? 'text-muted-foreground'
                    : 'text-primary-foreground'
              )}>
              {primaryLabel}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

function DesktopWebAttendeePreview({
  attendees,
  capacity,
  goingCount,
  canOpen,
  onPress,
}: {
  attendees: RadiousEventDetail['attendee_preview'];
  capacity: number;
  goingCount: number;
  canOpen: boolean;
  onPress: () => void;
}) {
  const previewAttendees = attendees.slice(0, 4);
  const meta = [
    goingCount >= 1 ? `${goingCount} ${goingCount === 1 ? 'person' : 'people'} going` : null,
    capacity > 0 ? `${capacity} cap` : null,
  ].filter(Boolean);

  return (
    <Pressable className="flex-row items-center" disabled={!canOpen} onPress={onPress}>
      {goingCount >= 1 && previewAttendees.length > 0 ? (
        <View className="mr-2 flex-row items-center">
          {previewAttendees.map((attendee, index) => (
            <View
              key={attendee.user_id}
              className="border-canvas size-7 items-center justify-center overflow-hidden rounded-full border bg-surface-raised"
              style={{ marginLeft: index === 0 ? 0 : -8 }}>
              {attendee.avatar_url ? (
                <Image
                  source={{ uri: attendee.avatar_url }}
                  style={{ width: 28, height: 28, borderRadius: 14 }}
                  contentFit="cover"
                  cachePolicy="memory-disk"
                />
              ) : (
                <Text className="text-[10px] font-semibold text-muted-foreground">
                  {(attendee.display_name || attendee.username || '?').slice(0, 2).toUpperCase()}
                </Text>
              )}
            </View>
          ))}
        </View>
      ) : null}
      <Text className="text-[13px] font-medium text-muted-foreground">{meta.join(' · ')}</Text>
    </Pressable>
  );
}

function WebEventActionModal({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <View className="absolute inset-0 z-[60] items-center justify-center bg-black/65 px-4">
      <Pressable
        accessibilityLabel="Close event action"
        className="absolute inset-0"
        onPress={onClose}
      />
      <View className="w-full max-w-[420px] rounded-[18px] border border-border bg-background p-4">
        {children}
      </View>
    </View>
  );
}

function WebGuestEventAction({
  title,
  action,
  onClose,
  onSignIn,
  onGetApp,
}: {
  title: string;
  action: 'rsvp' | 'manage';
  onClose: () => void;
  onSignIn: () => void;
  onGetApp: () => void;
}) {
  const heading = action === 'manage' ? 'Sign in to manage your plan' : 'Sign in to save your spot';

  return (
    <View className="gap-4">
      <View className="flex-row items-start justify-between gap-3">
        <View className="min-w-0 flex-1 gap-1">
          <Text className="text-[18px] font-semibold text-foreground">{heading}</Text>
          <Text className="text-[13px] leading-5 text-muted-foreground" numberOfLines={2}>
            {title}
          </Text>
        </View>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          className="size-9 items-center justify-center rounded-full border border-border bg-input/30"
          onPress={onClose}>
          <Icon as={XIcon} size={15} className="text-muted-foreground" />
        </Pressable>
      </View>

      <Text className="text-[13px] leading-5 text-muted-foreground">
        Radious keeps RSVPs, saved events, comments, and follows tied to your profile.
      </Text>

      <View className="gap-2">
        <Pressable
          className="h-11 items-center justify-center rounded-[14px] bg-primary"
          onPress={onSignIn}>
          <Text className="text-[14px] font-semibold text-primary-foreground">Sign in</Text>
        </Pressable>
        <Pressable
          className="h-11 items-center justify-center rounded-[14px] border border-border bg-input/20"
          onPress={onGetApp}>
          <Text className="text-[14px] font-semibold text-muted-foreground">Get the app</Text>
        </Pressable>
      </View>
    </View>
  );
}
